import {
  Add,
  ArrowDropDown,
  DeleteOutline,
  Refresh,
  Remove,
  Save,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  type ReactNode,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

import shape03 from "../../assets/crm-orders/selected-shape/figma/aviator.png";
import shape04 from "../../assets/crm-orders/selected-shape/figma/cat-eye.png";
import shape06C from "../../assets/crm-orders/selected-shape/figma/cut-away-oval.png";
import shape06 from "../../assets/crm-orders/selected-shape/figma/cut-away-rectangle.png";
import shape01 from "../../assets/crm-orders/selected-shape/figma/oblong.png";
import shape02 from "../../assets/crm-orders/selected-shape/figma/oval.png";
import shape06A from "../../assets/crm-orders/selected-shape/figma/rectangle.png";
import shape06B from "../../assets/crm-orders/selected-shape/figma/sharp-rectangle.png";
import shape05 from "../../assets/crm-orders/selected-shape/figma/symmetrical.png";
import { STATUS_CHIP_CONFIG } from "../../components/crm/OrderFilterControls";
import OrderStatusUpdateDialog from "../../components/crm/OrderStatusUpdateDialog";
import { useBreadcrumbs } from "../../contexts/BreadcrumbContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { getECPByParentGroup, getECPs } from "../../services/ecp.service";
import {
  deleteAdminOrder,
  getAdminOrder,
  getAdminProductAddons,
  getAdminProducts,
  updateAdminOrder,
} from "../../services/order.service";
import { downloadOrderXml } from "../../utils/orderXmlBuilder";
import { viewImage } from "../../services/upload.service";
import { ECP } from "../../types/ecp";
import {
  Order,
  OrderAddonItem,
  OrderStatus,
  OrderUpdateRequest,
  Product,
  ProductAddon,
  ProductAddonType,
} from "../../types/order";
import { normalizeDecimal, valueOrDash } from "../../utils/decimal";
import { payloadNum, clampAndFormat, formatSph, formatCyl, formatAdd, formatAxis } from "../../utils/lensFormatters";
import {
  getNestedRawValue as _getNestedRawValue,
  getValueFromPaths as _getValueFromPaths,
  displayValue,
  displayRaw,
  filterFieldInput,
} from "../../utils/orderDisplay";

const FRAME_TYPE_OPTIONS = [
  { value: "กรอบเต็มพลาสติก", label: "กรอบเต็มพลาสติก" },
  { value: "กรอบเต็มโลหะ", label: "กรอบเต็มโลหะ" },
  { value: "กรอบเซาะร่องเอ็น", label: "กรอบเซาะร่องเอ็น" },
  { value: "กรอบเซาะร่องโลหะ", label: "กรอบเซาะร่องโลหะ" },
  { value: "กรอบเจาะ", label: "กรอบเจาะ" },
];

const BASE_OPTIONS = [
  { value: "1.75", label: "1.75" },
  { value: "2.75", label: "2.75" },
  { value: "3.75", label: "3.75" },
  { value: "4.75", label: "4.75" },
  { value: "6", label: "6" },
];

const THAI_PREFIX_OPTIONS = [
  { value: "คุณ", label: "คุณ" },
  { value: "นาย", label: "นาย" },
  { value: "นาง", label: "นาง" },
  { value: "นางสาว", label: "นางสาว" },
];

const SHAPE_OPTIONS = [
  {
    value: "shape6b",
    label: "Sharp Rectangle",
    number: "9",
    image: shape06B,
  },
  { value: "shape3", label: "Aviator", number: "3", image: shape03 },
  { value: "shape6a", label: "Rectangle", number: "7", image: shape06A },
  { value: "shape4", label: "Cat Eye", number: "4", image: shape04 },
  { value: "shape2", label: "Oval", number: "2", image: shape02 },
  { value: "shape1", label: "Oblong", number: "1", image: shape01 },
  { value: "shape5", label: "Symmetrical", number: "5", image: shape05 },
  {
    value: "shape6",
    label: "Cut Away Rectangle",
    number: "6",
    image: shape06,
  },
  {
    value: "shape6c",
    label: "Cut Away Oval",
    number: "8",
    image: shape06C,
  },
];

const cellSx = {
  borderColor: "#E5E7EB",
  fontSize: 12,
  color: "#25272C",
  py: 1,
};

// ---- Ship-To merged list types & helpers ----

interface ShipToItem {
  ecpId?: string; // ECP ObjectId (ship_to.id in payload)
  id?: string; // Address id (ship_to.address.id in payload)
  code: string; // ECP code
  name: string; // Display name
  paymentTagName?: string;
  phone?: string;
  address?: string;
  sub_district?: string;
  district?: string;
  province?: string;
  zip_code?: string;
}

/** Get dedup key for a ShipToItem */
function shipToKey(item: ShipToItem): string {
  return item.id || item.code;
}

/** Find default item from merged list with fallback chain */
function findDefaultShipToItem(
  merged: ShipToItem[],
  currentShipToCode: string,
  currentAddressId: string | undefined,
  customerEcpCode: string,
): ShipToItem | undefined {
  if (merged.length === 0) return undefined;

  // 1. Exact match: same code + same address id
  if (currentAddressId) {
    const exact = merged.find(
      (i) => i.code === currentShipToCode && i.id === currentAddressId,
    );
    if (exact) return exact;
  }

  // 2. Match by code only (same ship-to code)
  if (currentShipToCode) {
    const byCode = merged.find((i) => i.code === currentShipToCode);
    if (byCode) return byCode;
  }

  // 3. First matching customerEcpCode
  if (customerEcpCode) {
    const byCustomer = merged.find((i) => i.code === customerEcpCode);
    if (byCustomer) return byCustomer;
  }

  // 4. First item
  return merged[0];
}

function findECPByCode(ecps: ECP[], code: string): ECP | undefined {
  const normalizedCode = code.trim().toUpperCase();
  return (
    ecps.find((ecp) => ecp.code.trim().toUpperCase() === normalizedCode) ||
    ecps[0]
  );
}

function convertECPsToShipToItems(ecps: ECP[]): ShipToItem[] {
  return ecps.map((ecp) => ({
    ecpId: ecp.id,
    code: ecp.code,
    name: ecp.name,
    paymentTagName: ecp.payment_tag_name,
  }));
}

function buildShipToItemsForSoldTo(ecps: ECP[], soldToCode: string): ShipToItem[] {
  const normalizedSoldToCode = soldToCode.trim().toUpperCase();
  const ownEcp = ecps.find(
    (ecp) => ecp.code.trim().toUpperCase() === normalizedSoldToCode,
  );
  const childECPs = ecps.filter(
    (ecp) =>
      ecp.code.trim().toUpperCase() !== normalizedSoldToCode &&
      ecp.ship_to_customer?.trim().toUpperCase() === normalizedSoldToCode,
  );

  return convertECPsToShipToItems([
    ...(ownEcp ? [ownEcp] : []),
    ...childECPs,
  ]);
}

function addShipToFallback(items: ShipToItem[], fallback?: ShipToItem): ShipToItem[] {
  if (!fallback?.code) return items;

  const fallbackKey = shipToKey(fallback);
  const alreadyIncluded = items.some(
    (item) =>
      shipToKey(item) === fallbackKey ||
      item.code.trim().toUpperCase() === fallback.code.trim().toUpperCase(),
  );

  return alreadyIncluded ? items : [...items, fallback];
}

function buildOrderShipToFallback(order: Order | null): ShipToItem | undefined {
  const code = order?.ship_to?.code || order?.customer?.ecp_code;
  if (!code) return undefined;

  const address =
    typeof order?.ship_to?.address === "object" && order.ship_to.address !== null
      ? order.ship_to.address
      : undefined;

  return {
    ecpId: order?.ship_to?.id,
    id: address?.id,
    code,
    name: order?.ship_to?.name || order?.customer?.shop_name || code,
    phone: address?.phone,
    address: address?.address,
    sub_district: address?.sub_district,
    district: address?.district,
    province: address?.province,
    zip_code: address?.zip_code,
  };
}

function formatDateTime(value: unknown): string {
  if (!value) return "-";
  const str = String(value).trim();
  if (!str) return "-";
  const date = new Date(str);
  if (isNaN(date.getTime())) return str;
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yy = String(date.getFullYear()).padStart(3, "0");
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${dd}/${mm}/${yy}, ${String(hours).padStart(2, "0")}:${minutes}:${seconds} ${ampm}`;
}

const getNestedRawValue = _getNestedRawValue;

function SectionCard({
  title,
  action,
  children,
  sx: sxProp,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  sx?: Record<string, unknown>;
}) {
  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 1,
        border: "1px solid #E5E7EB",
        boxShadow:
          "0px 1px 3px rgba(16, 24, 40, 0.08), 0px 1px 2px rgba(16, 24, 40, 0.08)",
        backgroundColor: "#FFFFFF",
        minWidth: 0,
        overflow: "hidden",
        ...sxProp,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 600,
            color: "#25272C",
            lineHeight: 1.4,
          }}
        >
          {title}
        </Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

function FieldLabel({
  children,
  required,
}: {
  children: string;
  required?: boolean;
}) {
  return (
    <Typography
      sx={{ fontSize: 12, fontWeight: 600, color: "#25272C", mb: 0.5 }}
    >
      {children}
      {required ? (
        <Box component="span" sx={{ color: "#BE2217", ml: 0.25 }}>
          *
        </Box>
      ) : null}
    </Typography>
  );
}

type PrescriptionField = "sphere" | "cylinder" | "axis" | "add";

const STEP_MAP: Record<PrescriptionField, number> = {
  sphere: 0.25,
  cylinder: 0.25,
  axis: 1,
  add: 0.25,
};

function StepperInput({
  field,
  value,
  onChange,
  onBlur,
  showStepper = true,
}: {
  field: PrescriptionField;
  value: string;
  onChange: (val: string) => void;
  onBlur: (val: string) => void;
  showStepper?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const step = STEP_MAP[field];
  const inputMode = field === "axis" ? "numeric" as const : "decimal" as const;
  const visible = showStepper && focused;

  const handleStep = (direction: 1 | -1) => {
    const num = parseFloat(value) || 0;
    const next = num + step * direction;
    onBlur(String(next));
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setFocused(false);
        }
      }}
    >
      <IconButton
        size="small"
        tabIndex={-1}
        onClick={() => handleStep(-1)}
        sx={{
          p: 0,
          color: "#8E95A2",
          opacity: visible ? 1 : 0,
          pointerEvents: visible ? "auto" : "none",
          transition: "opacity 0.15s",
          width: 20,
          minWidth: 20,
        }}
      >
        <Remove sx={{ fontSize: 16 }} />
      </IconButton>
      <TextField
        variant="standard"
        fullWidth
        type="text"
        slotProps={{ htmlInput: { inputMode } }}
        placeholder="-"
        value={value}
        onChange={(e) => {
          const filtered = filterFieldInput(field, e.target.value);
          onChange(filtered);
        }}
        onBlur={(e) => onBlur(e.target.value)}
        sx={{
          ...examInputSx,
          "& .MuiInput-input": { textAlign: "center", px: 0.25 },
        }}
      />
      <IconButton
        size="small"
        tabIndex={-1}
        onClick={() => handleStep(1)}
        sx={{
          p: 0,
          color: "#8E95A2",
          opacity: visible ? 1 : 0,
          pointerEvents: visible ? "auto" : "none",
          transition: "opacity 0.15s",
          width: 20,
          minWidth: 20,
        }}
      >
        <Add sx={{ fontSize: 16 }} />
      </IconButton>
    </Stack>
  );
}

interface ZoneOption {
  key: string;
  value: string;
  code: string;
  name: string;
}

function CustomerShippingZone({
  heading,
  subheading,
  codeValue,
  nameValue,
  onCodeChange,
  options,
  disabled,
  renderValue,
}: {
  heading: string;
  subheading?: string;
  codeValue: string;
  nameValue?: string;
  onCodeChange: (value: string) => void;
  options: ZoneOption[];
  disabled?: boolean;
  renderValue: (selected: string) => string;
}) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 600,
          color: "#8E95A2",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          mb: 1,
        }}
      >
        <Box
          component="span"
          sx={{
            color: "#101828",
            fontWeight: 700,
            fontSize: 14,
            textTransform: "none",
            letterSpacing: 0,
            mr: 0.75,
          }}
        >
          {heading}
        </Box>
        {subheading ? <>· {subheading}</> : null}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            md: "140px minmax(0, 1fr)",
          },
          alignItems: "center",
        }}
      >
        <Stack spacing={0.5} sx={{ py: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, color: "#8E95A2", lineHeight: 1.4 }}>
            ECP Code
          </Typography>
          <TextField
            select
            size="small"
            value={codeValue}
            onChange={(e) => onCodeChange(e.target.value)}
            disabled={disabled}
            SelectProps={{ renderValue: (s) => renderValue(String(s ?? "")) }}
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 0 },
              "& .MuiOutlinedInput-notchedOutline": {
                borderTop: 0,
                borderLeft: 0,
                borderRight: 0,
                borderRadius: 0,
              },
            }}
          >
            {options.map((opt) => (
              <MenuItem
                key={opt.key}
                value={opt.value}
                sx={{ alignItems: "flex-start", whiteSpace: "normal" }}
              >
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ minWidth: 0, width: "100%", alignItems: "flex-start" }}
                >
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      minWidth: 100,
                      flex: "0 0 auto",
                    }}
                  >
                    {opt.code}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: "#8E95A2",
                      flex: "1 1 auto",
                      minWidth: 0,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      wordBreak: "break-word",
                      lineHeight: 1.35,
                    }}
                  >
                    {opt.name}
                  </Typography>
                </Stack>
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Stack spacing={0.5} sx={{ py: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, color: "#8E95A2", lineHeight: 1.4 }}>
            ECP Name
          </Typography>
          <Box
            sx={{
              borderBottom: "1px solid #B6BAC3",
              minHeight: 30,
              display: "flex",
              alignItems: "center",
              pb: 0.75,
            }}
          >
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 400,
                color: "#101828",
                lineHeight: 1.4,
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              {valueOrDash(nameValue)}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}

const getValueFromPaths = _getValueFromPaths;

function getEyeData(
  source: Record<string, unknown> | undefined,
  eye: "re" | "le",
) {
  if (!source) return undefined;
  const aliases =
    eye === "re"
      ? ["re", "right", "od", "RE", "RIGHT", "OD"]
      : ["le", "left", "os", "LE", "LEFT", "OS"];

  for (const key of aliases) {
    const value = source[key];
    if (value && typeof value === "object") {
      return value as Record<string, unknown>;
    }
  }

  return undefined;
}

const editableValue = displayValue;

/** Decimal fields on each eye that should default to "0" when empty / "0E-6176" */
const EYE_DECIMAL_FIELDS = [
  "sphere", "sph", "cylinder", "cyl", "axis", "add", "pd", "height",
  "npd", "base", "et", "eh",
];

const EYE_FORMAT_MAP: Record<string, (v: string) => string> = {
  sphere: formatSph, sph: formatSph,
  cylinder: formatCyl, cyl: formatCyl,
  add: formatAdd,
  axis: formatAxis,
};

/** Normalise eye decimal fields so empty / Decimal128-zero values become "0",
 *  then apply sign formatting to power fields for initial display. */
function normalizeEyeDecimals(
  eye: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!eye || typeof eye !== "object") return eye;
  const out = { ...eye };
  for (const key of EYE_DECIMAL_FIELDS) {
    if (key in out) {
      const norm = normalizeDecimal(out[key]) || "0";
      const fmt = EYE_FORMAT_MAP[key];
      out[key] = fmt ? fmt(norm) : norm;
    }
  }
  return out;
}

function hasNonZeroValue(values: string[]) {
  return values.some((v) => v !== "" && v !== "-" && v !== "0" && v !== "0.00");
}

function detectPrismDecenterTab(
  prismValues: string[],
  decenterValues: string[],
): "Prism" | "Decenter" {
  const hasPrism = hasNonZeroValue(prismValues);
  const hasDecenter = hasNonZeroValue(decenterValues);
  if (hasDecenter && !hasPrism) return "Decenter";
  return "Prism";
}

function getQuadrantValues(
  source: Record<string, unknown> | undefined,
  aliases: string[],
) {
  const value = getValueFromPaths(source, aliases);
  if (!value || typeof value !== "object") {
    const fallback = normalizeDecimal(value);
    return [fallback || "", "", "", ""];
  }

  const map = value as Record<string, unknown>;
  return [
    normalizeDecimal(
      getValueFromPaths(map, [
        "field_1",
        "field1",
        "prism1",
        "p1",
        "d1",
        "left",
        "value1",
        "1",
      ]),
    ),
    normalizeDecimal(
      getValueFromPaths(map, [
        "dr1",
        "dir1",
        "direction1",
        "direction_1",
        "right",
        "value2",
        "2",
      ]),
    ),
    normalizeDecimal(
      getValueFromPaths(map, [
        "field_2",
        "field2",
        "prism2",
        "p2",
        "d2",
        "value3",
        "3",
      ]),
    ),
    normalizeDecimal(
      getValueFromPaths(map, [
        "dr2",
        "dir2",
        "direction2",
        "direction_2",
        "value4",
        "4",
      ]),
    ),
  ];
}

const prescriptionHeaderCellSx = {
  ...cellSx,
  backgroundColor: "#F2F2F2",
  fontSize: 13,
  fontWeight: 400,
  textAlign: "center",
  px: 1.5,
  py: 1.125,
  height: 42,
};

const prescriptionValueCellSx = {
  ...cellSx,
  fontSize: 13,
  fontWeight: 400,
  textAlign: "center",
  backgroundColor: "#FAFBFC",
  px: 1,
  py: 0.75,
  height: 48,
};

const examTableSx = {
  border: "1px solid #D8DBDF",
  borderRadius: "4px",
  overflow: "hidden",
  backgroundColor: "#FFFFFF",
};

const examHeaderCellSx = {
  ...cellSx,
  backgroundColor: "#F2F2F2",
  fontSize: 14,
  fontWeight: 400,
  color: "#25272C",
  px: 1.5,
  py: 1.125,
  height: 42,
};

const examRowLabelCellSx = {
  ...cellSx,
  width: 34,
  fontSize: 16,
  fontWeight: 400,
  color: "#25272C",
  textAlign: "center" as const,
  px: 0.5,
  py: 2,
  backgroundColor: "#FFFFFF",
};

const examValueCellSx = {
  ...cellSx,
  backgroundColor: "#FFFFFF",
  px: 1.5,
  py: 2,
  height: 64,
};

const examInputSx = {
  "& .MuiInput-root": {
    fontSize: 16,
    lineHeight: 1.4,
    color: "#25272C",
    "&:before": {
      borderBottomColor: "#B6BAC3",
    },
    "&:hover:not(.Mui-disabled):before": {
      borderBottomColor: "#B6BAC3",
    },
    "&:after": {
      borderBottomColor: "#1976D2",
    },
  },
  "& .MuiInputBase-input": {
    px: 0,
    py: 0.25,
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
  },
};

function firstNonEmptyValue(source: unknown, paths: string[]) {
  for (const path of paths) {
    const value = getNestedRawValue(source, path);
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text) return value;
  }
  return undefined;
}

function resolveExtraImageSrc(image: string | undefined) {
  if (!image) return "";
  const normalized = image.trim();
  if (!normalized) return "";

  if (
    /^(https?:)?\/\//i.test(normalized) ||
    normalized.startsWith("data:") ||
    normalized.startsWith("blob:")
  ) {
    return normalized;
  }

  const downloadPath = "/download/image/";
  const pathIndex = normalized.indexOf(downloadPath);
  if (pathIndex >= 0) {
    const fileName = normalized.slice(pathIndex + downloadPath.length);
    return fileName ? viewImage(fileName) : "";
  }

  if (normalized.startsWith("download/image/")) {
    return viewImage(normalized.slice("download/image/".length));
  }

  return viewImage(normalized);
}

function serializeAddonEntries(addons: OrderAddonItem[] | undefined) {
  return (
    addons
      ?.map((item) => {
        const addonId = item.product_addons?.id;
        if (!addonId) return null;
        return {
          addon_id: addonId,
          quantity: item.quantity ?? 1,
        };
      })
      .filter(
        (
          value,
        ): value is {
          addon_id: string;
          quantity: number;
        } => Boolean(value),
      ) || []
  );
}

function prepareOrderPayloadForSubmit(
  payload: OrderUpdateRequest,
): OrderUpdateRequest {
  const normalized = payload;

  // The Customer Comment field (`notes`) must be submitted as an empty string
  // when cleared, not converted to null with the other empty fields.
  if ((payload.notes ?? "").length === 0) {
    normalized.notes = "";
  }

  return normalized;
}

const TRACKED_FIELDS = [
  "lensProcess", "shape", "referenceNo", "notes", "orderType", "lensNumber",
  "productDescription", "purchasedItem", "transition", "coating", "indexValue",
  "firstName", "lastName", "frameName", "frameType", "trackingNumber",
  "remarks", "frameAssembly",
] as const;

const TRACKED_FIELD_LABELS: Record<(typeof TRACKED_FIELDS)[number], string> = {
  lensProcess: "Lens Process",
  shape: "Lens Shape",
  referenceNo: "Reference No.",
  notes: "Notes",
  orderType: "Order Type",
  lensNumber: "Lens Number",
  productDescription: "Product Description",
  purchasedItem: "Purchased Item",
  transition: "Transition",
  coating: "Coating",
  indexValue: "Index",
  firstName: "First Name",
  lastName: "Last Name",
  frameName: "Frame Name",
  frameType: "Frame Type",
  trackingNumber: "Tracking Number",
  remarks: "Details",
  frameAssembly: "Frame Assembly",
};

function computeSnapshot(state: { order: Order | null } & Record<string, unknown>): string {
  const fields: Record<string, unknown> = {};
  for (const key of TRACKED_FIELDS) {
    fields[key] = state[key];
  }
  fields._order = state.order ? JSON.stringify(state.order) : null;
  return JSON.stringify(fields);
}

type State = {
  order: Order | null;
  loading: boolean;
  submitting: boolean;
  lensProcess: string;
  shape: string;
  referenceNo: string;
  notes: string;
  orderType: string;
  lensNumber: string;
  productDescription: string;
  purchasedItem: string;
  transition: string;
  coating: string;
  indexValue: string;
  firstName: string;
  lastName: string;
  frameName: string;
  frameType: string;
  trackingNumber: string;
  remarks: string;
  frameAssembly: string;
  mergedShipToList: ShipToItem[];
  selectedShipToKey: string;
  savedSnapshot: string;
};

type Action =
  | { type: "SET_ORDER"; payload: Order | null }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_SUBMITTING"; payload: boolean }
  | { type: "SET_FIELD"; payload: { field: string; value: string } }
  | { type: "SET_ORDER_PATH"; payload: { path: string; value: unknown } }
  | { type: "SET_MERGED_SHIP_TO_LIST"; payload: ShipToItem[] }
  | { type: "SET_SELECTED_SHIP_TO_KEY"; payload: string }
  | {
    type: "INITIALIZE_ORDER";
    payload: { order: Order; lensProcess: string; shape: string };
  }
  | { type: "MARK_SAVED" };

const initialState: State = {
  order: null,
  loading: true,
  submitting: false,
  lensProcess: "PL",
  shape: "7",
  referenceNo: "",
  notes: "",
  orderType: "RX",
  lensNumber: "SINGLE",
  productDescription: "",
  purchasedItem: "",
  transition: "",
  coating: "",
  indexValue: "",
  firstName: "",
  lastName: "",
  frameName: "",
  frameType: "",
  trackingNumber: "",
  remarks: "",
  frameAssembly: "ส่งประกอบ",
  mergedShipToList: [],
  selectedShipToKey: "",
  savedSnapshot: "",
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "SET_ORDER":
      return { ...state, order: action.payload };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_SUBMITTING":
      return { ...state, submitting: action.payload };
    case "SET_FIELD":
      return { ...state, [action.payload.field]: action.payload.value };
    case "SET_ORDER_PATH": {
      if (!state.order) return state;
      const nextOrder = structuredClone(state.order) as Record<string, unknown>;
      const keys = action.payload.path.split(".");
      let current: Record<string, unknown> = nextOrder;

      keys.forEach((key, index) => {
        if (index === keys.length - 1) {
          current[key] = action.payload.value;
          return;
        }

        const nextValue = current[key];
        if (!nextValue || typeof nextValue !== "object") {
          current[key] = {};
        }
        current = current[key] as Record<string, unknown>;
      });

      return { ...state, order: nextOrder as Order };
    }
    case "SET_MERGED_SHIP_TO_LIST":
      return { ...state, mergedShipToList: action.payload };
    case "SET_SELECTED_SHIP_TO_KEY":
      return { ...state, selectedShipToKey: action.payload };
    case "INITIALIZE_ORDER": {
      const o = { ...action.payload.order };
      if (o.lens_detail) {
        o.lens_detail = {
          ...o.lens_detail,
          re: normalizeEyeDecimals(o.lens_detail.re as Record<string, unknown> | undefined) as typeof o.lens_detail.re,
          le: normalizeEyeDecimals(o.lens_detail.le as Record<string, unknown> | undefined) as typeof o.lens_detail.le,
        };
      }
      const newState = {
        ...state,
        order: o,
        lensProcess: action.payload.lensProcess,
        shape: action.payload.shape,
        loading: false,
        referenceNo: o.reference_no || "",
        notes: o.notes || "",
        orderType: o.order_type || "RX",
        lensNumber: o.lens_number || "SINGLE",
        productDescription:
          o.product_description || o.product?.custom_description || "",
        purchasedItem: o.detail?.purchased_item || "",
        transition: o.detail?.transition || "",
        coating: o.detail?.coating || "",
        indexValue: o.detail?.index || "",
        firstName: o.client?.name || "",
        lastName: o.client?.surname || "",
        frameName: String(getNestedRawValue(o, "frame_detail.name") || ""),
        frameType: String(getNestedRawValue(o, "frame_detail.type") || ""),
        frameAssembly: String(
          getNestedRawValue(o, "frame_detail.assembly") || "ส่งประกอบ",
        ),
        trackingNumber: Array.isArray(o.tracking_number)
          ? o.tracking_number.join(", ")
          : "",
        remarks: o.remarks || "",
      };
      return { ...newState, savedSnapshot: computeSnapshot(newState) };
    }
    case "MARK_SAVED":
      return { ...state, savedSnapshot: computeSnapshot(state) };
    default:
      return state;
  }
}

type StatusDialogState = {
  open: boolean;
  status: OrderStatus;
  details: string;
  trackingNumbers: { id: string; value: string }[];
  trackingDate: string;
  title?: string;
  subtitle?: string;
  submitLabel?: string;
  cancelLabel?: string;
};

export default function CrmOrdersEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { showSnackbar } = useSnackbar();

  const [state, dispatch] = useReducer(reducer, initialState);
  const [soldToOptions, setSoldToOptions] = useState<ECP[]>([]);
  const [soldToOptionsLoading, setSoldToOptionsLoading] = useState(false);
  const {
    order,
    loading,
    submitting,
    lensProcess,
    shape,
    referenceNo,
    notes,
    orderType,
    lensNumber,
    productDescription,
    purchasedItem,
    transition,
    coating,
    indexValue,
    firstName,
    lastName,
    frameName,
    frameType,
    trackingNumber,
    remarks,
    frameAssembly,
  } = state;
  const isDirty = useMemo(
    () => state.savedSnapshot !== "" && computeSnapshot(state) !== state.savedSnapshot,
    [state],
  );
  const mergedShipToList = state.mergedShipToList;
  const selectedShipToKey = state.selectedShipToKey;
  const setField = useCallback(
    (field: string, value: string) =>
      dispatch({ type: "SET_FIELD", payload: { field, value } }),
    [],
  );
  const setOrderPath = useCallback(
    (path: string, value: unknown) =>
      dispatch({ type: "SET_ORDER_PATH", payload: { path, value } }),
    [],
  );

  const applyShipToItem = useCallback(
    (item: ShipToItem) => {
      const key = shipToKey(item);
      dispatch({ type: "SET_SELECTED_SHIP_TO_KEY", payload: key });

      // Update ship_to code, name & id
      setOrderPath("ship_to.code", item.code);
      setOrderPath("ship_to.name", item.name);
      setOrderPath("ship_to.id", item.ecpId || "");

      // Populate ship_to.address only if address data exists
      if (item.id && (item.phone || item.address)) {
        setOrderPath("ship_to.address", {
          id: item.id,
          ecp_code: item.code,
          name: item.name,
          phone: item.phone || "",
          address: item.address || "",
          sub_district: item.sub_district || "",
          district: item.district || "",
          province: item.province || "",
          zip_code: item.zip_code || "",
        });
      } else {
        // Bare ECP entry — omit address
        setOrderPath("ship_to.address", undefined);
      }
    },
    [setOrderPath],
  );

  const syncShipToOptionsForSoldTo = useCallback(
    (ecps: ECP[], soldToCode: string, fallback?: ShipToItem) => {
      const shipToItems = addShipToFallback(
        buildShipToItemsForSoldTo(ecps, soldToCode),
        fallback,
      );

      dispatch({
        type: "SET_MERGED_SHIP_TO_LIST",
        payload: shipToItems,
      });

      if (shipToItems.length === 0) {
        dispatch({ type: "SET_SELECTED_SHIP_TO_KEY", payload: "" });
        return;
      }

      const currentKey = fallback ? shipToKey(fallback) : "";
      const selectedItem =
        shipToItems.find((item) => shipToKey(item) === currentKey) ||
        shipToItems[0];

      dispatch({
        type: "SET_SELECTED_SHIP_TO_KEY",
        payload: shipToKey(selectedItem),
      });
    },
    [],
  );

  const loadSoldToOptionsForECPCode = useCallback(
    async (ecpCode: string, fallbackShipTo?: ShipToItem) => {
      if (!ecpCode) return;

      setSoldToOptionsLoading(true);
      try {
        const currentEcpResponse = await getECPs({
          code: ecpCode,
          page_number: 1,
          page_size: 1,
        });
        const currentEcp = findECPByCode(currentEcpResponse?.ecps ?? [], ecpCode);
        const parentGroup = currentEcp?.parent_group?.trim();

        if (!parentGroup) {
          const nextOptions = currentEcp ? [currentEcp] : [];
          setSoldToOptions(nextOptions);
          syncShipToOptionsForSoldTo(nextOptions, ecpCode, fallbackShipTo);
          return;
        }

        const parentGroupECPs = await getECPByParentGroup(parentGroup);
        const nextOptions = parentGroupECPs ?? [];
        setSoldToOptions(nextOptions);
        syncShipToOptionsForSoldTo(nextOptions, ecpCode, fallbackShipTo);
      } catch (error) {
        console.error("Error loading Sold To ECP parent group:", error);
        showSnackbar("Cannot load Sold To ECP parent group", "error");
      } finally {
        setSoldToOptionsLoading(false);
      }
    },
    [showSnackbar, syncShipToOptionsForSoldTo],
  );

  const handleSelectSoldToCode = useCallback(
    (soldToCode: string) => {
      if (!soldToCode) return;

      const selectedSoldTo = soldToOptions.find(
        (ecp) => ecp.code === soldToCode,
      );

      setOrderPath("customer.ecp_code", soldToCode);
      setOrderPath("customer.shop_name", selectedSoldTo?.name || soldToCode);

      const fallbackItem: ShipToItem = {
        ecpId: selectedSoldTo?.id,
        code: soldToCode,
        name: selectedSoldTo?.name || soldToCode,
        paymentTagName: selectedSoldTo?.payment_tag_name,
      };

      // Rebuild the Ship To list against the current (possibly stale) ECP
      // set first so the auto-correct effect sees a list that contains the
      // new selection and doesn't immediately revert it. Then refetch the
      // parent group for the newly-selected Sold To.
      syncShipToOptionsForSoldTo(soldToOptions, soldToCode, fallbackItem);
      applyShipToItem(fallbackItem);
      void loadSoldToOptionsForECPCode(soldToCode, fallbackItem);
    },
    [
      applyShipToItem,
      loadSoldToOptionsForECPCode,
      setOrderPath,
      soldToOptions,
      syncShipToOptionsForSoldTo,
    ],
  );

  const handleSelectShipToItem = useCallback(
    (key: string) => {
      if (!key) return;
      const item = state.mergedShipToList.find((i) => shipToKey(i) === key);
      if (!item) return;
      applyShipToItem(item);
    },
    [state.mergedShipToList, applyShipToItem],
  );

  // Edge case #20: Reset selection if current key becomes invalid after data refresh
  useEffect(() => {
    if (mergedShipToList.length === 0 || !selectedShipToKey) return;

    const stillValid = mergedShipToList.some(
      (i) => shipToKey(i) === selectedShipToKey,
    );
    if (!stillValid) {
      const customerEcpCode = order?.customer?.ecp_code || "";
      const fallback = findDefaultShipToItem(
        mergedShipToList,
        "",
        undefined,
        customerEcpCode,
      );
      if (fallback) {
        applyShipToItem(fallback);
      }
    }
  }, [
    mergedShipToList,
    selectedShipToKey,
    order?.customer?.ecp_code,
    applyShipToItem,
  ]);

  const [productSearchResults, setProductSearchResults] = useState<Product[]>(
    [],
  );
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [selectedProductName, setSelectedProductName] = useState("");
  const [selectedProductSearch, setSelectedProductSearch] = useState("");
  const [selectedProductDesc, setSelectedProductDesc] = useState("");
  const [selectedProductDiameter, setSelectedProductDiameter] = useState("");
  const [availableAddons, setAvailableAddons] = useState<ProductAddon[]>([]);
  const [addonTypeFilter, setAddonTypeFilter] = useState<
    ProductAddonType | ""
  >("");
  const [selectedVaRows, setSelectedVaRows] = useState<string[]>([]);
  const [showVaAdder, setShowVaAdder] = useState(false);
  const [shapeSelectorAnchorEl, setShapeSelectorAnchorEl] =
    useState<HTMLElement | null>(null);

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: ReactNode;
    confirmLabel: string;
    confirmColor: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: "",
    description: "",
    confirmLabel: "",
    confirmColor: "#1976D2",
    onConfirm: () => { },
  });
  const [statusDialog, setStatusDialog] = useState<StatusDialogState>({
    open: false,
    status: "CREATED",
    details: "",
    trackingNumbers: [
      { id: Math.random().toString(36).substring(2, 9), value: "" },
    ],
    trackingDate: "",
  });

  const lensDetail = useMemo(
    () => (order?.lens_detail as Record<string, unknown> | undefined) ?? {},
    [order],
  );
  const reData = useMemo(() => getEyeData(lensDetail, "re"), [lensDetail]);
  const leData = useMemo(() => getEyeData(lensDetail, "le"), [lensDetail]);
  const leftPrismValues = useMemo(
    () => getQuadrantValues(leData, ["prism", "prism_value"]),
    [leData],
  );
  const rightDecenterValues = useMemo(
    () => getQuadrantValues(reData, ["decenter", "decenter_value"]),
    [reData],
  );
  const leftDecenterValues = useMemo(
    () => getQuadrantValues(leData, ["decenter", "decenter_value"]),
    [leData],
  );
  const rightPrismValues = useMemo(
    () => getQuadrantValues(reData, ["prism", "prism_value"]),
    [reData],
  );

  const [lePrismTab, setLePrismTab] = useState<"Prism" | "Decenter">("Prism");
  const [rePrismTab, setRePrismTab] = useState<"Prism" | "Decenter">("Prism");
  const [downloadingXml, setDownloadingXml] = useState(false);
  const [savingAndDownloading, setSavingAndDownloading] = useState(false);

  useEffect(() => {
    setLePrismTab(detectPrismDecenterTab(leftPrismValues, leftDecenterValues));
  }, [leftPrismValues, leftDecenterValues]);

  useEffect(() => {
    setRePrismTab(
      detectPrismDecenterTab(rightPrismValues, rightDecenterValues),
    );
  }, [rightPrismValues, rightDecenterValues]);
  const openConfirm = useCallback(
    (opts: {
      title: string;
      description: ReactNode;
      confirmLabel: string;
      confirmColor?: string;
      onConfirm: () => void;
    }) => {
      setConfirmDialog({
        open: true,
        title: opts.title,
        description: opts.description,
        confirmLabel: opts.confirmLabel,
        confirmColor: opts.confirmColor || "#1976D2",
        onConfirm: opts.onConfirm,
      });
    },
    [],
  );

  const closeConfirm = useCallback(() => {
    setConfirmDialog((prev) => ({ ...prev, open: false }));
  }, []);

  const openStatusDialog = useCallback(() => {
    setStatusDialog((prev) => ({
      ...prev,
      open: true,
      status: order?.status || "CREATED",
      trackingNumbers: [
        { id: Math.random().toString(36).substring(2, 9), value: "" },
      ],
      trackingDate: "",
      title: "Update Status Order",
      subtitle: "Please review carefully before updating the status.",
      submitLabel: "Update Status",
      cancelLabel: "Discard",
    }));
  }, [order?.status]);

  const closeStatusDialog = useCallback(() => {
    setStatusDialog((prev) => ({ ...prev, open: false }));
  }, []);

  useEffect(() => {
    setBreadcrumbs([
      { label: "Order List", to: "/crm/orders" },
      { label: "Edit Order List", to: `/crm/orders/${id}/edit` },
    ]);
  }, [id, setBreadcrumbs]);

  useEffect(() => {
    const run = async () => {
      if (!id) {
        navigate("/crm/orders");
        return;
      }

      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const data = await getAdminOrder(id);

        const corridorRaw = getNestedRawValue(data, "lens_detail.corridor");
        const corridorNorm = normalizeDecimal(corridorRaw);
        // If corridor has a real non-zero value, default to Corridor mode
        const initialLensProcess =
          corridorNorm && corridorNorm !== "0" ? "Comfort" : "PL";

        let initialShape = "7";
        const lensShape = getNestedRawValue(data, "frame_detail.lens_shape");
        if (
          typeof lensShape === "string" &&
          SHAPE_OPTIONS.some((item) => item.number === lensShape)
        ) {
          initialShape = lensShape;
        }

        dispatch({
          type: "INITIALIZE_ORDER",
          payload: {
            order: data,
            lensProcess: initialLensProcess,
            shape: initialShape,
          },
        });
        if (data.product) {
          const p = data.product;
          const parts = [
            p.search_string,
            p.custom_description || p.description,
            p.physical_diameter && `(${p.physical_diameter})`,
          ].filter(Boolean);
          const displayName = parts.length > 0 ? parts.join(" - ") : (data.product_description || "");
          setSelectedProductName(displayName);
          setSelectedProductSearch(p.search_string || "");
          setSelectedProductDesc(p.custom_description || p.description || "");
          setSelectedProductDiameter(p.physical_diameter || "");
        } else {
          setSelectedProductName(data.product_description || "");
        }

        const customerEcpCode = data.customer?.ecp_code || "";
        if (customerEcpCode) {
          await loadSoldToOptionsForECPCode(
            customerEcpCode,
            buildOrderShipToFallback(data),
          );
        }
      } catch (error) {
        console.error("Error loading order:", error);
        dispatch({ type: "SET_LOADING", payload: false });
      }
    };

    run();
  }, [id, navigate, loadSoldToOptionsForECPCode]);

  // Fetch available product addons for the VA selector
  const fetchAddons = useCallback(
    async (search?: string) => {
      try {
        const res = await getAdminProductAddons({
          page_size: 200,
          ...(addonTypeFilter ? { type: addonTypeFilter } : {}),
          ...(search ? { search } : {}),
        });
        setAvailableAddons(res.data || []);
      } catch (err) {
        console.error("Error loading product addons:", err);
      }
    },
    [addonTypeFilter],
  );

  useEffect(() => {
    fetchAddons();
  }, [fetchAddons]);

  // Debounced product master search for the product description
  const productSearchTimer = useMemo(() => ({ id: 0 as unknown as ReturnType<typeof setTimeout> }), []);
  const handleProductSearch = useCallback(
    (query: string) => {
      clearTimeout(productSearchTimer.id);
      if (!query.trim()) {
        setProductSearchResults([]);
        return;
      }
      setProductSearchLoading(true);
      productSearchTimer.id = setTimeout(async () => {
        try {
          const res = await getAdminProducts({
            search: query.trim(),
            page: 1,
            page_size: 20,
          });
          setProductSearchResults(res.data || []);
        } catch (err) {
          console.error("Error searching products:", err);
        } finally {
          setProductSearchLoading(false);
        }
      }, 300);
    },
    [productSearchTimer],
  );

  const handleAddAddon = useCallback(
    (addonId: string) => {
      if (!order || !addonId) return;
      const addon = availableAddons.find((a) => a.id === addonId);
      if (!addon) return;
      const current = order.addons || [];
      if (current.some((a) => a.product_addons?.id === addonId)) return;
      const newItem: OrderAddonItem = { product_addons: addon, quantity: 1 };
      setOrderPath("addons", [...current, newItem]);
    },
    [order, availableAddons, setOrderPath],
  );

  const orderStatusConfig = order?.status
    ? STATUS_CHIP_CONFIG[order.status]
    : undefined;
  const orderStatusLabel = useMemo(() => {
    if (orderStatusConfig?.label) return orderStatusConfig.label;
    if (!order?.status) return "Order Status";
    return order.status
      .toLowerCase()
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }, [order?.status, orderStatusConfig?.label]);

  const TERMINAL_STATUSES: OrderStatus[] = ["CANCELLED", "CREATED"];
  const isTerminal = TERMINAL_STATUSES.includes(order?.status as OrderStatus);

  const openCancelDialog = useCallback(() => {
    setStatusDialog({
      open: true,
      status: "CANCELLED",
      details: "",
      trackingNumbers: [
        { id: Math.random().toString(36).substring(2, 9), value: "" },
      ],
      trackingDate: "",
      title: "Cancel Order",
      subtitle: "Are you sure you want to cancel? Please provide a reason.",
      submitLabel: "Cancel Order",
      cancelLabel: "Discard",
    });
  }, []);

  const firstExtraImageSrc = useMemo(
    () => resolveExtraImageSrc(order?.extra_images?.[0]),
    [order?.extra_images],
  );
  const vaRows = useMemo(() => {
    return (order?.addons || []).map((item, index) => ({
      item: index + 1,
      index,
      addonItem: item,
      addon: item.product_addons,
      quantity: item.quantity ?? 1,
      rowId: `addon-${item.product_addons?.id || index}`,
    }));
  }, [order]);

  const hasAddons = (order?.addons || []).some(
    (item) => item.product_addons?.id === "69b7248393686c9fa26e99f7",
  );

  const handleDeleteSelectedVa = useCallback(() => {
    if (!order || selectedVaRows.length === 0) return;

    const selected = new Set(selectedVaRows);
    setOrderPath(
      "addons",
      (order.addons || []).filter(
        (item, index) =>
          !selected.has(`addon-${item.product_addons?.id || index}`),
      ),
    );
    setSelectedVaRows([]);
  }, [order, selectedVaRows, setOrderPath]);

  const customerTitle = firstNonEmptyValue(order, [
    "client.title",
    "client.prefix",
    "client.salutation",
    "customer.title",
    "customer.prefix",
    "customer.salutation",
  ]);
  const customerTitleValue = customerTitle ? String(customerTitle) : "คุณ";
  const handleCustomerTitleChange = useCallback(
    (value: string) => {
      setOrderPath("client.title", value);
      setOrderPath("customer.title", value);
    },
    [setOrderPath],
  );

  const buildOrderPayload = useCallback(
    (
      status: OrderStatus,
      opts?: {
        details?: string;
        trackingNumbers?: { id: string; value: string }[];
        trackingDate?: string;
      },
    ): OrderUpdateRequest => {
      const isCreated = status === "CREATED";
      const trackingArr = opts?.trackingNumbers
        ? opts.trackingNumbers.map((t) => t.value.trim()).filter(Boolean)
        : [];

      const shipToAddressRaw =
        typeof order?.ship_to?.address === "object" &&
        order?.ship_to?.address !== null
          ? order.ship_to.address
          : undefined;
      const hasShipToAddress = Boolean(
        shipToAddressRaw &&
          (shipToAddressRaw.phone ||
            shipToAddressRaw.address ||
            shipToAddressRaw.sub_district ||
            shipToAddressRaw.district ||
            shipToAddressRaw.province ||
            shipToAddressRaw.zip_code),
      );
      const shipToAddressPayload = hasShipToAddress
        ? {
            id: String(shipToAddressRaw?.id || ""),
            ecp_code: String(
              shipToAddressRaw?.ecp_code || order?.ship_to?.code || "",
            ),
            name: String(
              shipToAddressRaw?.name || order?.ship_to?.name || "",
            ),
            phone: String(shipToAddressRaw?.phone || ""),
            address: String(shipToAddressRaw?.address || ""),
            sub_district: String(shipToAddressRaw?.sub_district || ""),
            district: String(shipToAddressRaw?.district || ""),
            province: String(shipToAddressRaw?.province || ""),
            zip_code: String(shipToAddressRaw?.zip_code || ""),
          }
        : undefined;

      return {
        addons: serializeAddonEntries(order?.addons),
        client: {
          title: customerTitleValue,
          name: firstName,
          surname: lastName,
        },
        // Sold-To is sent as a root-level `ecp_code` string (backend's
        // expected shape). The `customer` object on the order is owned
        // by the backend and is not updated through this endpoint.
        ecp_code: order?.customer?.ecp_code || undefined,
        detail: {
          coating,
          index: indexValue,
          purchased_item: purchasedItem,
          transition,
        },
        extra_images: order?.extra_images || [],
        frame_detail: {
          ...(order?.frame_detail || {}),
          assembly: frameAssembly,
          dbl: payloadNum(getNestedRawValue(order, "frame_detail.dbl")),
          height: payloadNum(getNestedRawValue(order, "frame_detail.height")),
          lens_shape: shape,
          name: frameName,
          pantoscopic_tilt: payloadNum(
            getNestedRawValue(order, "frame_detail.pantoscopic_tilt"),
          ),
          type: frameType,
          vertex_distance: payloadNum(
            getNestedRawValue(order, "frame_detail.vertex_distance"),
          ),
          warp_angle: payloadNum(
            getNestedRawValue(order, "frame_detail.warp_angle"),
          ),
          width: payloadNum(getNestedRawValue(order, "frame_detail.width")),
        },
        lens_detail: {
          ...(order?.lens_detail || {}),
          bc: payloadNum(getNestedRawValue(order, "lens_detail.bc")),
          corridor: payloadNum(
            getNestedRawValue(order, "lens_detail.corridor"),
          ),
          lens_circle: payloadNum(
            getNestedRawValue(order, "lens_detail.lens_circle"),
          ),
          min_et: payloadNum(getNestedRawValue(order, "lens_detail.min_et")),
          pl: payloadNum(getNestedRawValue(order, "lens_detail.pl")),
          reading_distance: payloadNum(
            getNestedRawValue(order, "lens_detail.reading_distance"),
          ),
          re: getNestedRawValue(order, "lens_detail.re"),
          le: getNestedRawValue(order, "lens_detail.le"),
        },
        lens_number: lensNumber as OrderUpdateRequest["lens_number"],
        notes,
        order_type: orderType as OrderUpdateRequest["order_type"],
        product_description: productDescription,
        product_id: order?.product?.id || undefined,
        reference_no: referenceNo,
        remarks:
          opts?.details !== undefined
            ? opts.details.trim() || undefined
            : remarks || undefined,
        ship_to: {
          code: String(order?.ship_to?.code || ""),
          id: String(order?.ship_to?.id || ""),
          name: String(order?.ship_to?.name || ""),
          ...(shipToAddressPayload ? { address: shipToAddressPayload } : {}),
        },
        status,
        tracking_number:
          isCreated && trackingArr.length ? trackingArr : undefined,
        tracking_date: isCreated && opts?.trackingDate
          ? `${opts.trackingDate}T00:00:00Z`
          : undefined,
      };
    },
    [
      order, customerTitleValue, firstName, lastName, coating, indexValue,
      purchasedItem, transition, frameAssembly, shape, frameName, frameType,
      lensNumber, notes, orderType, productDescription, referenceNo, remarks,
    ],
  );

  const handleUpdateStatus = async (
    status: OrderStatus,
    details?: string,
    dialogTrackingNumbers?: { id: string; value: string }[],
    dialogTrackingDate?: string,
  ) => {
    if (!id) return;
    dispatch({ type: "SET_SUBMITTING", payload: true });
    try {
      const payload = buildOrderPayload(status, {
        details,
        trackingNumbers: dialogTrackingNumbers,
        trackingDate: dialogTrackingDate,
      });
      await updateAdminOrder(id, prepareOrderPayloadForSubmit(payload));
      showSnackbar("Order status updated successfully", "success");
      navigate("/crm/orders");
    } catch (error) {
      console.error("Error updating order status:", error);
      showSnackbar("Failed to update order status", "error");
    } finally {
      dispatch({ type: "SET_SUBMITTING", payload: false });
    }
  };

  const getPendingChanges = useCallback(() => {
    if (!state.savedSnapshot) return [];

    const before = JSON.parse(state.savedSnapshot) as Record<string, unknown>;
    const after = JSON.parse(computeSnapshot(state)) as Record<string, unknown>;

    const changes = TRACKED_FIELDS.flatMap((field) => {
      const previousValue = before[field];
      const nextValue = after[field];
      if (JSON.stringify(previousValue) === JSON.stringify(nextValue)) {
        return [];
      }
      return [{ label: TRACKED_FIELD_LABELS[field], previousValue, nextValue }];
    });

    if (JSON.stringify(before._order) !== JSON.stringify(after._order)) {
      changes.push({
        label: "Order details",
        previousValue: "Saved version",
        nextValue: "Updated version",
      });
    }

    return changes;
  }, [state]);

  const renderSaveChanges = useCallback(() => {
    const changes = getPendingChanges();

    if (changes.length === 0) {
      return "No changes detected.";
    }

    return (
      <Stack spacing={1.25}>
        <Typography sx={{ fontSize: 14, color: "#4B5563" }}>
          Please review these changes before saving.
        </Typography>
        <Stack
          spacing={1}
          sx={{
            maxHeight: 280,
            overflow: "auto",
            border: "1px solid #E5E7EB",
            borderRadius: 1,
            p: 1.25,
            bgcolor: "#FAFBFC",
          }}
        >
          {changes.map((change, index) => (
            <Box key={`${change.label}-${index}`}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#25272C" }}>
                {change.label}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#6B7280", wordBreak: "break-word" }}>
                {String(change.previousValue ?? "-")} → {String(change.nextValue ?? "-")}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Stack>
    );
  }, [getPendingChanges]);

  const performSave = async () => {
    if (!id || !order) return;
    dispatch({ type: "SET_SUBMITTING", payload: true });
    try {
      const intendedSoldToCode = order.customer?.ecp_code?.trim() || "";
      const payload = buildOrderPayload(order.status as OrderStatus);
      await updateAdminOrder(id, prepareOrderPayloadForSubmit(payload));
      const freshOrder = await getAdminOrder(id);
      const persistedSoldToCode = freshOrder.customer?.ecp_code?.trim() || "";
      // Only refetch ECP options if backend canonicalized the Sold To to a
      // different code — otherwise the options already in state (from the
      // user's earlier pick) are still valid, so we skip the network round-
      // trip and avoid an extra render after save.
      const needsRefetch =
        !!freshOrder.customer?.ecp_code &&
        intendedSoldToCode !== persistedSoldToCode;
      if (needsRefetch) {
        await loadSoldToOptionsForECPCode(
          freshOrder.customer!.ecp_code!,
          buildOrderShipToFallback(freshOrder),
        );
      }
      // Batch the post-save reducer updates as a non-urgent transition so
      // React commits them together and keeps the UI responsive — React 18
      // pattern for state updates that follow an async boundary.
      startTransition(() => {
        dispatch({ type: "SET_ORDER", payload: freshOrder });
        dispatch({ type: "MARK_SAVED" });
      });
      if (intendedSoldToCode && intendedSoldToCode !== persistedSoldToCode) {
        showSnackbar(
          "Order saved, but Sold To wasn't updated (backend doesn't accept Sold To changes yet)",
          "warning",
        );
      } else {
        showSnackbar("Order saved successfully", "success");
      }
    } catch (error) {
      console.error("Error saving order:", error);
      showSnackbar("Failed to save order", "error");
    } finally {
      dispatch({ type: "SET_SUBMITTING", payload: false });
    }
  };

  const performSaveAndDownloadXml = async () => {
    if (!id || !order) return;
    setSavingAndDownloading(true);
    try {
      const intendedSoldToCode = order.customer?.ecp_code?.trim() || "";
      const payload = buildOrderPayload(order.status as OrderStatus);
      await updateAdminOrder(id, prepareOrderPayloadForSubmit(payload));
      const freshOrder = await getAdminOrder(id);
      await downloadOrderXml(freshOrder);
      const persistedSoldToCode = freshOrder.customer?.ecp_code?.trim() || "";
      const needsRefetch =
        !!freshOrder.customer?.ecp_code &&
        intendedSoldToCode !== persistedSoldToCode;
      if (needsRefetch) {
        await loadSoldToOptionsForECPCode(
          freshOrder.customer!.ecp_code!,
          buildOrderShipToFallback(freshOrder),
        );
      }
      startTransition(() => {
        dispatch({ type: "SET_ORDER", payload: freshOrder });
        dispatch({ type: "MARK_SAVED" });
      });
      if (intendedSoldToCode && intendedSoldToCode !== persistedSoldToCode) {
        showSnackbar(
          "Order saved & XML downloaded, but Sold To wasn't updated (backend doesn't accept Sold To changes yet)",
          "warning",
        );
      } else {
        showSnackbar("Order saved & XML downloaded", "success");
      }
    } catch (error) {
      console.error("Error saving & downloading:", error);
      showSnackbar("Failed to save & download XML", "error");
    } finally {
      setSavingAndDownloading(false);
    }
  };

  const handleSave = () => {
    openConfirm({
      title: "Review changes before saving",
      description: renderSaveChanges(),
      confirmLabel: "Save",
      confirmColor: "#111111",
      onConfirm: () => {
        closeConfirm();
        void performSave();
      },
    });
  };

  const handleSaveAndDownloadXml = () => {
    openConfirm({
      title: "Review changes before saving & downloading XML",
      description: renderSaveChanges(),
      confirmLabel: "Save & Download XML",
      confirmColor: "#111111",
      onConfirm: () => {
        closeConfirm();
        void performSaveAndDownloadXml();
      },
    });
  };

  const handleDelete = async () => {
    if (!id) return;
    dispatch({ type: "SET_SUBMITTING", payload: true });
    try {
      await deleteAdminOrder(id);
      showSnackbar("Order deleted successfully", "success");
      navigate("/crm/orders");
    } catch (error) {
      console.error("Error deleting order:", error);
      showSnackbar("Failed to delete order", "error");
    } finally {
      dispatch({ type: "SET_SUBMITTING", payload: false });
    }
  };

  const handleDownloadXml = async () => {
    if (!order) return;
    setDownloadingXml(true);
    try {
      await downloadOrderXml(order);
    } catch (error) {
      console.error("Error downloading XML:", error);
      showSnackbar("Failed to download XML", "error");
    } finally {
      setDownloadingXml(false);
    }
  };

  if (loading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ minHeight: 300 }}
      >
        <CircularProgress size={28} />
      </Stack>
    );
  }

  const paymentTagName = firstNonEmptyValue(order, [
    "payment_tag_name",
    "payment_term",
    "detail.payment_term",
  ]);
  const createdDate = firstNonEmptyValue(order, ["created_at", "createdAt"]);
  const shippingDate = firstNonEmptyValue(order, [
    "shipping_date",
    "shipped_at",
    "shippingDate",
  ]);
  const trackingDate = firstNonEmptyValue(order, [
    "tracking_date",
    "trackingDate",
  ]);
  const selectedShapeOption =
    SHAPE_OPTIONS.find((item) => item.number === shape) || SHAPE_OPTIONS[0];
  return (
    <>
      <Box sx={{ backgroundColor: "#F3F4F6", minHeight: "100%", pb: { xs: 30, sm: 12 } }}>
        <Stack spacing={3}>
          <Box
            sx={{
              display: "grid",
              gap: 3,
              alignItems: "start",
              gridTemplateColumns: {
                xs: "1fr",
                lg: "minmax(0, 1fr) 440px",
              },
            }}
          >
            <Stack spacing={2.5} sx={{ minWidth: 0 }}>
              <Box sx={{ display: "grid", gap: 2.5 }}>
                <SectionCard title="Order Status">
                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Chip
                        label={orderStatusLabel}
                        size="small"
                        sx={{
                          borderRadius: "4px",
                          backgroundColor: orderStatusConfig?.bg || "#D4EDFF",
                          color: orderStatusConfig?.color || "#0054FC",
                        }}
                      />
                      {order?.order_id && (
                        <Typography sx={{ fontSize: 12, color: "#8E95A2" }}>
                          {order.order_id}
                        </Typography>
                      )}
                    </Stack>

                    <Box>
                      <FieldLabel>Details</FieldLabel>
                      <TextField
                        multiline
                        minRows={3}
                        placeholder="Message detail"
                        value={remarks}
                        onChange={(e) => setField("remarks", e.target.value)}
                        fullWidth
                        disabled={submitting || isTerminal}
                      />
                    </Box>
                  </Stack>
                </SectionCard>

                <Box
                  sx={{
                    display: "grid",
                    gap: 2.5,
                    gridTemplateColumns: {
                      xs: "1fr",
                      lg: "minmax(0, 1fr) minmax(0, 1fr)",
                    },
                  }}
                >
                  <Stack spacing={2.5} sx={{ height: "100%" }}>
                    <SectionCard title="Order Variants">
                      <Stack spacing={2}>
                        <Box>
                          <FieldLabel required>Order Type</FieldLabel>
                          <RadioGroup
                            row
                            value={orderType}
                            onChange={(e) =>
                              setField("orderType", e.target.value)
                            }
                            sx={{ mt: 0.5, columnGap: 3 }}
                          >
                            <FormControlLabel
                              value="RX"
                              control={<Radio size="small" sx={{ color: "#1976D2", "&.Mui-checked": { color: "#1976D2" } }} />}
                              label="RX"
                              sx={{
                                mr: 0,
                                minWidth: 196,
                                py: 1,
                                "& .MuiFormControlLabel-label": {
                                  fontSize: 16,
                                  color: "#25272C",
                                },
                              }}
                            />
                          </RadioGroup>
                        </Box>
                        <Box>
                          <FieldLabel required>Lens Type for Order</FieldLabel>
                          <RadioGroup
                            row
                            value={lensNumber}
                            onChange={(e) =>
                              setField("lensNumber", e.target.value)
                            }
                            sx={{ mt: 0.5, columnGap: 2 }}
                          >
                            <FormControlLabel
                              value="SINGLE"
                              control={<Radio size="small" sx={{ color: "#1976D2", "&.Mui-checked": { color: "#1976D2" } }} />}
                              label="Single Lens"
                              sx={{
                                mr: 0,
                                py: 1,
                                "& .MuiFormControlLabel-label": {
                                  fontSize: 16,
                                  color: "#25272C",
                                },
                              }}
                            />
                            <FormControlLabel
                              value="PAIR"
                              control={<Radio size="small" sx={{ color: "#1976D2", "&.Mui-checked": { color: "#1976D2" } }} />}
                              label="Pair of Lenses"
                              sx={{
                                mr: 0,
                                py: 1,
                                "& .MuiFormControlLabel-label": {
                                  fontSize: 16,
                                  color: "#25272C",
                                },
                              }}
                            />
                          </RadioGroup>
                        </Box>
                      </Stack>
                    </SectionCard>

                    <SectionCard
                      title="Customer Comment"
                      sx={{ flex: 1, display: "flex", flexDirection: "column" }}
                    >
                      <Stack spacing={1} sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 16,
                            fontWeight: 400,
                            color: "#25272C",
                            lineHeight: 1.4,
                          }}
                        >
                          Comment
                        </Typography>
                        <TextField
                          multiline
                          minRows={5}
                          fullWidth
                          value={notes}
                          onChange={(e) => setField("notes", e.target.value)}
                          size="small"
                          placeholder="Add a customer note"
                          sx={{
                            flex: 1,
                            "& .MuiOutlinedInput-root": {
                              alignItems: "flex-start",
                              borderRadius: 1,
                              px: 0.5,
                              height: "100%",
                            },
                            "& .MuiOutlinedInput-input": {
                              fontSize: 16,
                              lineHeight: 1.4,
                              color: "#111111",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#D8DBDF",
                            },
                          }}
                        />
                      </Stack>
                    </SectionCard>
                  </Stack>

                  <Stack spacing={2.5}>
                    <SectionCard title="Customer & Shipping">
                      <Stack spacing={2}>
                        <CustomerShippingZone
                          heading="Sold To"
                          subheading="ลูกค้าที่ออกบิล"
                          codeValue={order?.customer?.ecp_code || ""}
                          nameValue={order?.customer?.shop_name}
                          onCodeChange={handleSelectSoldToCode}
                          disabled={soldToOptionsLoading}
                          renderValue={(selected) => String(selected || "")}
                          options={
                            order?.customer?.ecp_code &&
                            !soldToOptions.some(
                              (ecp) => ecp.code === order.customer?.ecp_code,
                            )
                              ? [
                                  {
                                    key: order.customer.ecp_code,
                                    value: order.customer.ecp_code,
                                    code: order.customer.ecp_code,
                                    name: order.customer.shop_name || "-",
                                  },
                                  ...soldToOptions.map((ecp) => ({
                                    key: ecp.id || ecp.code,
                                    value: ecp.code,
                                    code: ecp.code,
                                    name: ecp.name || "-",
                                  })),
                                ]
                              : soldToOptions.map((ecp) => ({
                                  key: ecp.id || ecp.code,
                                  value: ecp.code,
                                  code: ecp.code,
                                  name: ecp.name || "-",
                                }))
                          }
                        />
                        <Box sx={{ borderTop: "1px solid #EEF0F3", pt: 2 }}>
                          <CustomerShippingZone
                            heading="Ship To"
                            subheading="ผู้รับสินค้า"
                            codeValue={state.selectedShipToKey}
                            nameValue={
                              order?.ship_to?.name ||
                              order?.customer?.shop_name
                            }
                            onCodeChange={handleSelectShipToItem}
                            disabled={
                              soldToOptionsLoading ||
                              state.mergedShipToList.length === 0
                            }
                            renderValue={(selected) => {
                              const item = state.mergedShipToList.find(
                                (shipTo) => shipToKey(shipTo) === selected,
                              );
                              return item?.code || String(selected || "");
                            }}
                            options={state.mergedShipToList.map((item) => ({
                              key: shipToKey(item),
                              value: shipToKey(item),
                              code: item.code,
                              name: item.name || "-",
                            }))}
                          />
                        </Box>
                      </Stack>
                    </SectionCard>

                    <SectionCard title="Terms & Notes">
                      <Box
                        sx={{
                          display: "grid",
                          gap: 2,
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "minmax(0, 1fr) minmax(0, 1fr)",
                          },
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 16,
                            fontWeight: 400,
                            color: "#25272C",
                            lineHeight: 1.4,
                          }}
                        >
                          Payment Term
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 16,
                            fontWeight: 400,
                            color: "#25272C",
                            lineHeight: 1.4,
                          }}
                        >
                          {valueOrDash(paymentTagName)}
                        </Typography>
                      </Box>
                    </SectionCard>
                  </Stack>
                </Box>
              </Box>

              <SectionCard title="Full Name">
                <Stack spacing={1.5}>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                    <Box sx={{ flex: 1 }}>
                      <FieldLabel required>Title</FieldLabel>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={customerTitleValue}
                        onChange={(e) =>
                          handleCustomerTitleChange(e.target.value)
                        }
                      >
                        {THAI_PREFIX_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <FieldLabel required>First Name</FieldLabel>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Enter first name"
                        value={firstName}
                        onChange={(e) => setField("firstName", e.target.value)}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <FieldLabel required>Last Name</FieldLabel>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Enter last name"
                        value={lastName}
                        onChange={(e) => setField("lastName", e.target.value)}
                      />
                    </Box>
                  </Stack>
                  <Box sx={{ maxWidth: 360 }}>
                    <FieldLabel required>Order Reference</FieldLabel>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Enter reference no."
                      value={referenceNo}
                      onChange={(e) => setField("referenceNo", e.target.value)}
                    />
                  </Box>
                </Stack>
              </SectionCard>

              <SectionCard title="Product Description">
                <Box
                  sx={{
                    display: "grid",
                    gap: 3,
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "minmax(0, 1fr) minmax(0, 1fr)",
                    },
                    alignItems: "start",
                  }}
                >
                  <Box sx={{ py: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: "#8E95A2",
                        lineHeight: 1.4,
                        mb: 0.5,
                      }}
                    >
                      Product Description Details
                    </Typography>
                    <Autocomplete<Product, false, false, true>
                      freeSolo
                      options={productSearchResults}
                      getOptionLabel={(opt) => {
                        if (typeof opt === "string") return opt;
                        const parts = [
                          opt.search_string,
                          opt.custom_description || opt.description,
                          opt.physical_diameter && `(${opt.physical_diameter})`,
                        ].filter(Boolean);
                        return parts.join(" - ");
                      }}
                      filterOptions={(x) => x}
                      loading={productSearchLoading}
                      inputValue={productDescription}
                      onInputChange={(_e, value, reason) => {
                        if (reason === "input") {
                          setField("productDescription", value);
                          handleProductSearch(value);
                        }
                        if (reason === "clear") {
                          setField("productDescription", "");
                          setSelectedProductName("");
                          setSelectedProductSearch("");
                          setSelectedProductDesc("");
                          setSelectedProductDiameter("");
                          setOrderPath("product.id", undefined);
                          setProductSearchResults([]);
                        }
                      }}
                      onChange={(_e, val) => {
                        if (!val || typeof val === "string") {
                          if (!val) {
                            setField("productDescription", "");
                            setSelectedProductName("");
                            setSelectedProductSearch("");
                            setSelectedProductDesc("");
                            setSelectedProductDiameter("");
                            setOrderPath("product.id", undefined);
                          }
                          return;
                        }
                        const parts = [
                          val.search_string,
                          val.custom_description || val.description,
                          val.physical_diameter && `(${val.physical_diameter})`,
                        ].filter(Boolean);
                        const displayName = parts.join(" - ");
                        setField("productDescription", displayName);
                        setSelectedProductName(displayName);
                        setSelectedProductSearch(val.search_string || "");
                        setSelectedProductDesc(val.custom_description || val.description || "");
                        setSelectedProductDiameter(val.physical_diameter || "");
                        setOrderPath("product.id", val.id);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          fullWidth
                          placeholder="Search product"
                          slotProps={{
                            input: {
                              ...params.InputProps,
                              sx: {
                                fontSize: 16,
                                fontWeight: 400,
                                color: "#111111",
                                lineHeight: 1.4,
                                "&:before": {
                                  borderBottomColor: "#B6BAC3",
                                },
                                "&:hover:not(.Mui-disabled):before": {
                                  borderBottomColor: "#B6BAC3",
                                },
                                "&:after": {
                                  borderBottomColor: "#1976D2",
                                },
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ py: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: "#8E95A2",
                        lineHeight: 1.4,
                        mb: 0.5,
                      }}
                    >
                      Transition/ทำสี
                    </Typography>
                    <TextField
                      variant="standard"
                      fullWidth
                      placeholder="Enter transition"
                      value={transition}
                      onChange={(e) => setField("transition", e.target.value)}
                      InputProps={{
                        disableUnderline: false,
                        sx: {
                          fontSize: 16,
                          fontWeight: 400,
                          color: "#25272C",
                          lineHeight: 1.4,
                          "&:before": {
                            borderBottomColor: "#B6BAC3",
                          },
                          "&:hover:not(.Mui-disabled):before": {
                            borderBottomColor: "#B6BAC3",
                          },
                          "&:after": {
                            borderBottomColor: "#1976D2",
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>
              </SectionCard>

              <SectionCard title="Prescription Details">
                <Stack spacing={3}>
                  <Box sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                    <Box sx={{ ...examTableSx, minWidth: 880 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{ ...examHeaderCellSx, width: 34 }}
                            />
                            <TableCell
                              sx={{ ...examHeaderCellSx }}
                            >
                              Product
                            </TableCell>
                            <TableCell sx={{ ...examHeaderCellSx, width: 120, textAlign: "center" }}>SPH</TableCell>
                            <TableCell sx={{ ...examHeaderCellSx, width: 120, textAlign: "center" }}>CYL</TableCell>
                            <TableCell sx={{ ...examHeaderCellSx, width: 100, textAlign: "center" }}>AXIS</TableCell>
                            <TableCell sx={{ ...examHeaderCellSx, width: 120, textAlign: "center" }}>ADD</TableCell>
                            <TableCell sx={{ ...examHeaderCellSx, width: 80, textAlign: "center" }}>PL</TableCell>
                            <TableCell
                              sx={{ ...examHeaderCellSx, width: 80, textAlign: "center", whiteSpace: "nowrap" }}
                            >
                              Dont Prod
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {[
                            {
                              key: "re",
                              label: "R",
                              data: reData,
                              path: "lens_detail.re",
                            },
                            {
                              key: "le",
                              label: "L",
                              data: leData,
                              path: "lens_detail.le",
                            },
                          ].map((eye) => (
                            <TableRow key={eye.key}>
                              <TableCell sx={examRowLabelCellSx}>
                                {eye.label}
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...examValueCellSx,
                                  textAlign: "left",
                                  minWidth: 250,
                                }}
                              >
                                {selectedProductName ? (
                                  <Typography
                                    sx={{
                                      fontSize: 13,
                                      color: "#25272C",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {selectedProductSearch ? (
                                      <>
                                        <Box component="span" sx={{ fontWeight: 600 }}>{selectedProductSearch}</Box>
                                        {selectedProductDesc && (
                                          <Box component="span" sx={{ fontWeight: 400, color: "#8E95A2" }}>{` - ${selectedProductDesc}`}</Box>
                                        )}
                                        {selectedProductDiameter && (
                                          <Box component="span" sx={{ fontWeight: 500 }}>{` - (${selectedProductDiameter})`}</Box>
                                        )}
                                      </>
                                    ) : (
                                      selectedProductName
                                    )}
                                  </Typography>
                                ) : (
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.5}
                                  >
                                    <Box
                                      sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: "50%",
                                        backgroundColor: "#F5A623",
                                        flexShrink: 0,
                                      }}
                                    />
                                    <Typography
                                      sx={{
                                        fontSize: 13,
                                        color: "#8E95A2",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      No product selected
                                    </Typography>
                                  </Stack>
                                )}
                              </TableCell>
                              <TableCell sx={{ ...examValueCellSx, px: 0.5, textAlign: "center" }}>
                                <StepperInput
                                  field="sphere"
                                  value={editableValue(eye.data, [
                                    "sphere",
                                    "sph",
                                  ])}
                                  showStepper={!hasAddons}
                                  onChange={(val) =>
                                    setOrderPath(`${eye.path}.sphere`, val)
                                  }
                                  onBlur={(val) =>
                                    setOrderPath(
                                      `${eye.path}.sphere`,
                                      clampAndFormat("sphere", val),
                                    )
                                  }
                                />
                              </TableCell>
                              <TableCell sx={{ ...examValueCellSx, px: 0.5, textAlign: "center" }}>
                                <StepperInput
                                  field="cylinder"
                                  value={editableValue(eye.data, [
                                    "cylinder",
                                    "cyl",
                                  ])}
                                  showStepper={!hasAddons}
                                  onChange={(val) =>
                                    setOrderPath(`${eye.path}.cylinder`, val)
                                  }
                                  onBlur={(val) =>
                                    setOrderPath(
                                      `${eye.path}.cylinder`,
                                      clampAndFormat("cylinder", val),
                                    )
                                  }
                                />
                              </TableCell>
                              <TableCell sx={{ ...examValueCellSx, px: 0.5, textAlign: "center" }}>
                                <StepperInput
                                  field="axis"
                                  value={editableValue(eye.data, ["axis"])}
                                  showStepper={!hasAddons}
                                  onChange={(val) =>
                                    setOrderPath(`${eye.path}.axis`, val)
                                  }
                                  onBlur={(val) =>
                                    setOrderPath(
                                      `${eye.path}.axis`,
                                      clampAndFormat("axis", val),
                                    )
                                  }
                                />
                              </TableCell>
                              <TableCell sx={{ ...examValueCellSx, px: 0.5, textAlign: "center" }}>
                                <StepperInput
                                  field="add"
                                  value={editableValue(eye.data, ["add"])}
                                  showStepper={!hasAddons}
                                  onChange={(val) =>
                                    setOrderPath(`${eye.path}.add`, val)
                                  }
                                  onBlur={(val) =>
                                    setOrderPath(
                                      `${eye.path}.add`,
                                      clampAndFormat("add", val),
                                    )
                                  }
                                />
                              </TableCell>
                              {eye.key === "re" && (
                                <TableCell
                                  rowSpan={2}
                                  sx={{
                                    ...examValueCellSx,
                                    textAlign: "center",
                                    verticalAlign: "middle",
                                  }}
                                >
                                  <TextField
                                    variant="standard"
                                    fullWidth
                                    type="number"
                                    placeholder="0.00"
                                    value={displayRaw(
                                      getNestedRawValue(order, "lens_detail.pl"),
                                    )}
                                    onChange={(e) =>
                                      setOrderPath("lens_detail.pl", e.target.value)
                                    }
                                    sx={{
                                      ...examInputSx,
                                      "& .MuiInput-input": { textAlign: "center" },
                                    }}
                                  />
                                </TableCell>
                              )}
                              <TableCell
                                sx={{
                                  ...examValueCellSx,
                                  textAlign: "center",
                                  verticalAlign: "middle",
                                }}
                              >
                                <Checkbox
                                  checked={Boolean(
                                    getNestedRawValue(
                                      order,
                                      `${eye.path}.dont_prod`,
                                    ),
                                  )}
                                  onChange={(e) =>
                                    setOrderPath(
                                      `${eye.path}.dont_prod`,
                                      e.target.checked,
                                    )
                                  }
                                  sx={{ p: 0.5, color: "#1976D2", "&.Mui-checked": { color: "#1976D2" } }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gap: 2.5,
                      gridTemplateColumns: {
                        xs: "1fr",
                        lg: "minmax(0, 1fr) minmax(0, 1fr)",
                      },
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 20,
                          fontWeight: 600,
                          color: "#25272C",
                          lineHeight: 1.4,
                          mb: 2,
                        }}
                      >
                        Height
                      </Typography>
                      <Box sx={{ overflowX: "auto" }}>
                      <Box sx={{ ...examTableSx, minWidth: 280 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{ ...examHeaderCellSx, width: 34 }}
                              />
                              <TableCell sx={examHeaderCellSx}>PH</TableCell>
                              <TableCell sx={examHeaderCellSx}>PD</TableCell>
                              <TableCell sx={examHeaderCellSx}>NPD</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {[
                              {
                                key: "re",
                                label: "R",
                                data: reData,
                                path: "lens_detail.re",
                              },
                              {
                                key: "le",
                                label: "L",
                                data: leData,
                                path: "lens_detail.le",
                              },
                            ].map((eye) => (
                              <TableRow key={`height-${eye.key}`}>
                                <TableCell sx={examRowLabelCellSx}>
                                  {eye.label}
                                </TableCell>
                                <TableCell sx={examValueCellSx}>
                                  <TextField
                                    variant="standard"
                                    fullWidth
                                    placeholder="-"
                                    value={editableValue(eye.data, [
                                      "height",
                                      "ph",
                                      "h",
                                    ])}
                                    onChange={(e) =>
                                      setOrderPath(
                                        `${eye.path}.height`,
                                        e.target.value,
                                      )
                                    }
                                    sx={examInputSx}
                                  />
                                </TableCell>
                                <TableCell sx={examValueCellSx}>
                                  <TextField
                                    variant="standard"
                                    fullWidth
                                    placeholder="-"
                                    value={editableValue(eye.data, ["pd"])}
                                    onChange={(e) =>
                                      setOrderPath(
                                        `${eye.path}.pd`,
                                        e.target.value,
                                      )
                                    }
                                    sx={examInputSx}
                                  />
                                </TableCell>
                                <TableCell sx={examValueCellSx}>
                                  <TextField
                                    variant="standard"
                                    fullWidth
                                    placeholder="-"
                                    value={editableValue(eye.data, ["npd"])}
                                    onChange={(e) =>
                                      setOrderPath(
                                        `${eye.path}.npd`,
                                        e.target.value,
                                      )
                                    }
                                    sx={examInputSx}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                      </Box>
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 20,
                          fontWeight: 600,
                          color: "#25272C",
                          lineHeight: 1.4,
                          mb: 2,
                        }}
                      >
                        Base
                      </Typography>
                      <Box sx={{ overflowX: "auto" }}>
                      <Box sx={{ ...examTableSx, minWidth: 280 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{ ...examHeaderCellSx, width: 34 }}
                              />
                              <TableCell sx={examHeaderCellSx}>Base</TableCell>
                              <TableCell sx={examHeaderCellSx}>ET</TableCell>
                              <TableCell sx={examHeaderCellSx}>MinCT</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {[
                              {
                                key: "re",
                                label: "R",
                                basePath: "lens_detail.re.base",
                                etPath: "lens_detail.re.et",
                              },
                              {
                                key: "le",
                                label: "L",
                                basePath: "lens_detail.le.base",
                                etPath: "lens_detail.le.et",
                              },
                            ].map((eye) => (
                              <TableRow key={`base-${eye.key}`}>
                                <TableCell sx={examRowLabelCellSx}>
                                  {eye.label}
                                </TableCell>
                                <TableCell sx={examValueCellSx}>
                                  <TextField
                                    select
                                    variant="standard"
                                    fullWidth
                                    value={normalizeDecimal(
                                      getNestedRawValue(order, eye.basePath),
                                    )}
                                    onChange={(e) =>
                                      setOrderPath(eye.basePath, e.target.value)
                                    }
                                    sx={examInputSx}
                                  >
                                    <MenuItem value="">-</MenuItem>
                                    {BASE_OPTIONS.map((opt) => (
                                      <MenuItem
                                        key={opt.value}
                                        value={opt.value}
                                      >
                                        {opt.label}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </TableCell>
                                <TableCell sx={examValueCellSx}>
                                  <TextField
                                    variant="standard"
                                    fullWidth
                                    placeholder="-"
                                    value={normalizeDecimal(
                                      getNestedRawValue(order, eye.etPath),
                                    )}
                                    onChange={(e) =>
                                      setOrderPath(eye.etPath, e.target.value)
                                    }
                                    sx={examInputSx}
                                  />
                                </TableCell>
                                <TableCell sx={examValueCellSx}>
                                  <TextField
                                    variant="standard"
                                    fullWidth
                                    placeholder="-"
                                    value={normalizeDecimal(
                                      getNestedRawValue(
                                        order,
                                        "lens_detail.min_et",
                                      ),
                                    )}
                                    onChange={(e) =>
                                      setOrderPath(
                                        "lens_detail.min_et",
                                        e.target.value,
                                      )
                                    }
                                    sx={examInputSx}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                      </Box>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gap: 2.5,
                      gridTemplateColumns: {
                        xs: "1fr",
                        lg: "minmax(0, 1fr) minmax(0, 1fr)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 20,
                          fontWeight: 600,
                          color: "#25272C",
                          lineHeight: 1.4,
                          mb: 2,
                        }}
                      >
                        Shape
                      </Typography>
                      <Box sx={{ ...examTableSx, minWidth: 0, flex: 1, overflowX: "auto" }}>
                        <Table size="small" sx={{ minWidth: 480 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{ ...examHeaderCellSx, width: 48, whiteSpace: "nowrap" }}
                              >
                                Trace
                              </TableCell>
                              <TableCell sx={{ ...examHeaderCellSx, whiteSpace: "nowrap" }}>
                                Shapes
                              </TableCell>
                              <TableCell sx={{ ...examHeaderCellSx, whiteSpace: "nowrap" }}>
                                Req Dia Right
                              </TableCell>
                              <TableCell sx={{ ...examHeaderCellSx, whiteSpace: "nowrap" }}>
                                Req Dia Left
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell
                                sx={{
                                  ...examValueCellSx,
                                  textAlign: "center",
                                }}
                              >
                                <Checkbox
                                  checked={Boolean(
                                    getNestedRawValue(
                                      order,
                                      "frame_detail.trace",
                                    ),
                                  )}
                                  onChange={(e) =>
                                    setOrderPath(
                                      "frame_detail.trace",
                                      e.target.checked,
                                    )
                                  }
                                  sx={{ p: 0.5, color: "#1976D2", "&.Mui-checked": { color: "#1976D2" } }}
                                />
                              </TableCell>
                              <TableCell sx={examValueCellSx}>
                                <Box
                                  onClick={(event) =>
                                    setShapeSelectorAnchorEl(
                                      event.currentTarget,
                                    )
                                  }
                                  sx={{
                                    minWidth: 160,
                                    maxWidth: 220,
                                    cursor: "pointer",
                                    borderBottom: "1px solid #B6BAC3",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    minHeight: 30,
                                    pb: 0.25,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: 16,
                                      fontWeight: 400,
                                      color: "#25272C",
                                      lineHeight: 1.4,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {selectedShapeOption.label}
                                  </Typography>
                                  <ArrowDropDown
                                    sx={{
                                      color: "#25272C",
                                      fontSize: 22,
                                      flexShrink: 0,
                                    }}
                                  />
                                </Box>
                                <Popover
                                  open={Boolean(shapeSelectorAnchorEl)}
                                  anchorEl={shapeSelectorAnchorEl}
                                  onClose={() => setShapeSelectorAnchorEl(null)}
                                  anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "left",
                                  }}
                                  transformOrigin={{
                                    vertical: "top",
                                    horizontal: "left",
                                  }}
                                  slotProps={{
                                    paper: {
                                      sx: {
                                        p: 2,
                                        mt: 1,
                                        borderRadius: 1,
                                        width: {
                                          xs: 332,
                                          sm: 498,
                                          md: 814,
                                        },
                                        maxWidth: "calc(100vw - 32px)",
                                        boxShadow:
                                          "0px 8px 24px rgba(16, 24, 40, 0.12)",
                                      },
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: {
                                        xs: "repeat(2, minmax(0, 1fr))",
                                        sm: "repeat(3, minmax(0, 1fr))",
                                        md: "repeat(5, minmax(0, 1fr))",
                                      },
                                      gap: 2,
                                      alignItems: "start",
                                    }}
                                  >
                                    {SHAPE_OPTIONS.map((item) => {
                                      const selected = shape === item.number;
                                      return (
                                        <Box
                                          key={item.number}
                                          onClick={() => {
                                            setField("shape", item.number);
                                            setShapeSelectorAnchorEl(null);
                                          }}
                                          sx={{
                                            width: "100%",
                                            minHeight: 142,
                                            borderRadius: 1,
                                            border: `1.438px solid ${selected ? "#1976D2" : "#D8DBDF"
                                              }`,
                                            backgroundColor: "#FFFFFF",
                                            overflow: "hidden",
                                            cursor: "pointer",
                                            transition:
                                              "border-color 0.2s ease, box-shadow 0.2s ease",
                                            boxShadow: selected
                                              ? "0 0 0 1px rgba(25, 118, 210, 0.08)"
                                              : "none",
                                          }}
                                        >
                                          <Stack
                                            alignItems="center"
                                            spacing={0.75}
                                            sx={{ px: 1.25, py: 1.25 }}
                                          >
                                            <Box
                                              sx={{
                                                width: 100,
                                                height: 94,
                                                position: "relative",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                              }}
                                            >
                                              <Box
                                                component="img"
                                                src={item.image}
                                                alt={item.label}
                                                sx={{
                                                  width: 100,
                                                  height: 94,
                                                  objectFit: "contain",
                                                  display: "block",
                                                }}
                                              />
                                              <Typography
                                                sx={{
                                                  position: "absolute",
                                                  fontSize: 18,
                                                  fontWeight: 700,
                                                  color: "#111111",
                                                  lineHeight: 1.4,
                                                }}
                                              >
                                                {item.number}
                                              </Typography>
                                            </Box>
                                            <Typography
                                              sx={{
                                                fontSize: 14,
                                                fontWeight: 400,
                                                color: "#25272C",
                                                lineHeight: 1.3,
                                                textAlign: "center",
                                                minHeight: 36,
                                                display: "flex",
                                                alignItems: "flex-start",
                                                justifyContent: "center",
                                              }}
                                            >
                                              {item.label}
                                            </Typography>
                                          </Stack>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                </Popover>
                              </TableCell>
                              <TableCell sx={examValueCellSx}>
                                <TextField
                                  variant="standard"
                                  fullWidth
                                  placeholder="-"
                                  value={normalizeDecimal(
                                    getNestedRawValue(
                                      order,
                                      "frame_detail.req_dia_right",
                                    ),
                                  )}
                                  onChange={(e) =>
                                    setOrderPath(
                                      "frame_detail.req_dia_right",
                                      e.target.value,
                                    )
                                  }
                                  sx={examInputSx}
                                />
                              </TableCell>
                              <TableCell sx={examValueCellSx}>
                                <TextField
                                  variant="standard"
                                  fullWidth
                                  placeholder="-"
                                  value={normalizeDecimal(
                                    getNestedRawValue(
                                      order,
                                      "frame_detail.req_dia_left",
                                    ),
                                  )}
                                  onChange={(e) =>
                                    setOrderPath(
                                      "frame_detail.req_dia_left",
                                      e.target.value,
                                    )
                                  }
                                  sx={examInputSx}
                                />
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 20,
                          fontWeight: 600,
                          color: "#25272C",
                          lineHeight: 1.4,
                          mb: 2,
                        }}
                      >
                        Frame Details
                      </Typography>
                      <Box sx={{ ...examTableSx, minWidth: 0, flex: 1, overflowX: "auto" }}>
                        <Table size="small" sx={{ minWidth: 380, tableLayout: "fixed" }}>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ ...examHeaderCellSx, width: "20%" }}>A</TableCell>
                              <TableCell sx={{ ...examHeaderCellSx, width: "20%" }}>B</TableCell>
                              <TableCell sx={{ ...examHeaderCellSx, width: "20%" }}>DBL</TableCell>
                              <TableCell sx={{ ...examHeaderCellSx, width: "40%" }}>
                                Frame
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell sx={{ ...examValueCellSx, minWidth: 60 }}>
                                <TextField
                                  variant="standard"
                                  fullWidth
                                  placeholder="-"
                                  value={normalizeDecimal(
                                    getNestedRawValue(
                                      order,
                                      "frame_detail.width",
                                    ),
                                  )}
                                  onChange={(e) =>
                                    setOrderPath(
                                      "frame_detail.width",
                                      e.target.value,
                                    )
                                  }
                                  sx={examInputSx}
                                />
                              </TableCell>
                              <TableCell sx={{ ...examValueCellSx, minWidth: 60 }}>
                                <TextField
                                  variant="standard"
                                  fullWidth
                                  placeholder="-"
                                  value={normalizeDecimal(
                                    getNestedRawValue(
                                      order,
                                      "frame_detail.height",
                                    ),
                                  )}
                                  onChange={(e) =>
                                    setOrderPath(
                                      "frame_detail.height",
                                      e.target.value,
                                    )
                                  }
                                  sx={examInputSx}
                                />
                              </TableCell>
                              <TableCell sx={{ ...examValueCellSx, minWidth: 60 }}>
                                <TextField
                                  variant="standard"
                                  fullWidth
                                  placeholder="-"
                                  value={normalizeDecimal(
                                    getNestedRawValue(
                                      order,
                                      "frame_detail.dbl",
                                    ),
                                  )}
                                  onChange={(e) =>
                                    setOrderPath(
                                      "frame_detail.dbl",
                                      e.target.value,
                                    )
                                  }
                                  sx={examInputSx}
                                />
                              </TableCell>
                              <TableCell sx={{ ...examValueCellSx, minWidth: 120 }}>
                                <TextField
                                  select
                                  variant="standard"
                                  fullWidth
                                  value={frameType}
                                  onChange={(e) =>
                                    setField("frameType", e.target.value)
                                  }
                                  sx={examInputSx}
                                >
                                  {FRAME_TYPE_OPTIONS.map((opt) => (
                                    <MenuItem
                                      key={opt.value}
                                      value={opt.value}
                                    >
                                      {opt.label}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Box>
                    </Box>
                  </Box>
                </Stack>
              </SectionCard>

              <SectionCard title="Lens Power">
                <FieldLabel required>Select Lens Power</FieldLabel>
                <RadioGroup
                  value={lensProcess}
                  onChange={(e) => setField("lensProcess", e.target.value)}
                  sx={{ mt: 1, gap: 0 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      px: 2,
                      py: 1.25,
                      borderRadius: "8px 8px 0 0",
                      border: "1px solid",
                      borderColor: lensProcess === "PL" ? "#1976D2" : "#E5E7EB",
                      backgroundColor: lensProcess === "PL" ? "#F0F7FF" : "#FAFBFC",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <FormControlLabel
                      value="PL"
                      control={
                        <Radio
                          size="small"
                          sx={{
                            color: "#B6BAC3",
                            "&.Mui-checked": { color: "#1976D2" },
                          }}
                        />
                      }
                      label="PL"
                      sx={{
                        mr: 0,
                        minWidth: 80,
                        "& .MuiFormControlLabel-label": {
                          fontSize: 14,
                          fontWeight: lensProcess === "PL" ? 600 : 400,
                          color: lensProcess === "PL" ? "#1976D2" : "#8E95A2",
                        },
                      }}
                    />
                    <TextField
                      variant="standard"
                      placeholder="-"
                      disabled={lensProcess !== "PL"}
                      value={normalizeDecimal(
                        getNestedRawValue(order, "lens_detail.pl"),
                      )}
                      onChange={(e) =>
                        setOrderPath("lens_detail.pl", e.target.value)
                      }
                      sx={{
                        ...examInputSx,
                        flex: 1,
                        maxWidth: 160,
                        opacity: lensProcess === "PL" ? 1 : 0.4,
                        transition: "opacity 0.15s ease",
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      px: 2,
                      py: 1.25,
                      borderRadius: "0 0 8px 8px",
                      border: "1px solid",
                      borderTop: "none",
                      borderColor: lensProcess === "Comfort" ? "#1976D2" : "#E5E7EB",
                      backgroundColor: lensProcess === "Comfort" ? "#F0F7FF" : "#FAFBFC",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <FormControlLabel
                      value="Comfort"
                      control={
                        <Radio
                          size="small"
                          sx={{
                            color: "#B6BAC3",
                            "&.Mui-checked": { color: "#1976D2" },
                          }}
                        />
                      }
                      label="Corridor"
                      sx={{
                        mr: 0,
                        minWidth: 80,
                        "& .MuiFormControlLabel-label": {
                          fontSize: 14,
                          fontWeight: lensProcess === "Comfort" ? 600 : 400,
                          color: lensProcess === "Comfort" ? "#1976D2" : "#8E95A2",
                        },
                      }}
                    />
                    <TextField
                      variant="standard"
                      placeholder="-"
                      disabled={lensProcess !== "Comfort"}
                      value={normalizeDecimal(
                        getNestedRawValue(order, "lens_detail.corridor"),
                      )}
                      onChange={(e) =>
                        setOrderPath(
                          "lens_detail.corridor",
                          e.target.value,
                        )
                      }
                      sx={{
                        ...examInputSx,
                        flex: 1,
                        maxWidth: 160,
                        opacity: lensProcess === "Comfort" ? 1 : 0.4,
                        transition: "opacity 0.15s ease",
                      }}
                    />
                  </Box>
                </RadioGroup>
              </SectionCard>

              <SectionCard title="Prism Details">
                <Box sx={{ overflowX: "auto" }}>
                  <Box sx={{ ...examTableSx, minWidth: 770 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{ ...examHeaderCellSx, width: 34 }}
                          />
                          <TableCell sx={{ ...examHeaderCellSx, width: 240 }}>
                            Prism/Decenter
                          </TableCell>
                          <TableCell sx={{ ...examHeaderCellSx, width: 80 }}>
                            Value
                          </TableCell>
                          <TableCell sx={{ ...examHeaderCellSx, width: 80 }}>
                            DR1
                          </TableCell>
                          <TableCell sx={{ ...examHeaderCellSx, width: 80 }}>
                            Value
                          </TableCell>
                          <TableCell sx={{ ...examHeaderCellSx, width: 80 }}>
                            DR2
                          </TableCell>
                          <TableCell sx={{ ...examHeaderCellSx, width: 256 }}>
                            Thinning
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[
                          {
                            key: "re",
                            label: "R",
                            tab: rePrismTab,
                            setTab: setRePrismTab,
                            values:
                              rePrismTab === "Prism"
                                ? rightPrismValues
                                : rightDecenterValues,
                            paths:
                              rePrismTab === "Prism"
                                ? [
                                  "lens_detail.re.prism.field_1",
                                  "lens_detail.re.prism.dr1",
                                  "lens_detail.re.prism.field_2",
                                  "lens_detail.re.prism.dr2",
                                ]
                                : [
                                  "lens_detail.re.decenter.field_1",
                                  "lens_detail.re.decenter.dr1",
                                  "lens_detail.re.decenter.field_2",
                                  "lens_detail.re.decenter.dr2",
                                ],
                          },
                          {
                            key: "le",
                            label: "L",
                            tab: lePrismTab,
                            setTab: setLePrismTab,
                            values:
                              lePrismTab === "Prism"
                                ? leftPrismValues
                                : leftDecenterValues,
                            paths:
                              lePrismTab === "Prism"
                                ? [
                                  "lens_detail.le.prism.field_1",
                                  "lens_detail.le.prism.dr1",
                                  "lens_detail.le.prism.field_2",
                                  "lens_detail.le.prism.dr2",
                                ]
                                : [
                                  "lens_detail.le.decenter.field_1",
                                  "lens_detail.le.decenter.dr1",
                                  "lens_detail.le.decenter.field_2",
                                  "lens_detail.le.decenter.dr2",
                                ],
                          },
                        ].map((row) => (
                          <TableRow key={row.key}>
                            <TableCell sx={examRowLabelCellSx}>
                              {row.label}
                            </TableCell>
                            <TableCell sx={examValueCellSx}>
                              <TextField
                                select
                                variant="standard"
                                fullWidth
                                value={row.tab}
                                onChange={(e) =>
                                  row.setTab(
                                    e.target.value as "Prism" | "Decenter",
                                  )
                                }
                                sx={examInputSx}
                                SelectProps={{
                                  displayEmpty: true,
                                }}
                              >
                                <MenuItem value="Prism">Prism</MenuItem>
                                <MenuItem value="Decenter">Decenter</MenuItem>
                              </TextField>
                            </TableCell>
                            <TableCell sx={examValueCellSx}>
                              <TextField
                                variant="standard"
                                fullWidth
                                placeholder="-"
                                value={row.values[0]}
                                onChange={(e) =>
                                  setOrderPath(row.paths[0], e.target.value)
                                }
                                sx={examInputSx}
                              />
                            </TableCell>
                            <TableCell sx={examValueCellSx}>
                              <TextField
                                variant="standard"
                                fullWidth
                                type="number"
                                placeholder="-"
                                value={row.values[1]}
                                onChange={(e) =>
                                  setOrderPath(row.paths[1], e.target.value)
                                }
                                sx={examInputSx}
                              />
                            </TableCell>
                            <TableCell sx={examValueCellSx}>
                              <TextField
                                variant="standard"
                                fullWidth
                                type="number"
                                placeholder="-"
                                value={row.values[2]}
                                onChange={(e) =>
                                  setOrderPath(row.paths[2], e.target.value)
                                }
                                sx={examInputSx}
                              />
                            </TableCell>
                            <TableCell sx={examValueCellSx}>
                              <TextField
                                variant="standard"
                                fullWidth
                                type="number"
                                placeholder="-"
                                value={row.values[3]}
                                onChange={(e) =>
                                  setOrderPath(row.paths[3], e.target.value)
                                }
                                sx={examInputSx}
                              />
                            </TableCell>
                            <TableCell sx={examValueCellSx}>
                              <TextField
                                select
                                variant="standard"
                                fullWidth
                                value={lensProcess}
                                onChange={(e) =>
                                  setField("lensProcess", e.target.value)
                                }
                                sx={examInputSx}
                                SelectProps={{
                                  displayEmpty: true,
                                  renderValue: (value) => {
                                    const label =
                                      value === "Comfort"
                                        ? "Comfort"
                                        : "Without (Cancel Prism)";
                                    return (
                                      <Box
                                        component="span"
                                        sx={{ color: "#8E95A2" }}
                                      >
                                        {label}
                                      </Box>
                                    );
                                  },
                                }}
                              >
                                <MenuItem value="PL">
                                  Without (Cancel Prism)
                                </MenuItem>
                                <MenuItem value="Comfort">Comfort</MenuItem>
                              </TextField>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              </SectionCard>

              <SectionCard title="F360">
                <Box sx={{ ...examTableSx, minWidth: 0 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...examHeaderCellSx, whiteSpace: "nowrap" }}>
                          Wrap Angle
                        </TableCell>
                        <TableCell sx={{ ...examHeaderCellSx, whiteSpace: "nowrap" }}>
                          Pantoscopic Angle
                        </TableCell>
                        <TableCell sx={{ ...examHeaderCellSx, whiteSpace: "nowrap" }}>
                          Vertex
                        </TableCell>
                        <TableCell sx={{ ...examHeaderCellSx, whiteSpace: "nowrap" }}>
                          Reading Distance
                        </TableCell>
                        <TableCell sx={{ ...examHeaderCellSx, whiteSpace: "nowrap" }}>
                          Nose Bridge Value (NVB)
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={examValueCellSx}>
                          <TextField
                            variant="standard"
                            fullWidth
                            type="number"
                            placeholder="0"
                            value={normalizeDecimal(
                              getNestedRawValue(
                                order,
                                "frame_detail.warp_angle",
                              ),
                            )}
                            onChange={(e) =>
                              setOrderPath(
                                "frame_detail.warp_angle",
                                e.target.value,
                              )
                            }
                            sx={examInputSx}
                          />
                        </TableCell>
                        <TableCell sx={examValueCellSx}>
                          <TextField
                            variant="standard"
                            fullWidth
                            type="number"
                            placeholder="0"
                            value={normalizeDecimal(
                              getNestedRawValue(
                                order,
                                "frame_detail.pantoscopic_tilt",
                              ),
                            )}
                            onChange={(e) =>
                              setOrderPath(
                                "frame_detail.pantoscopic_tilt",
                                e.target.value,
                              )
                            }
                            sx={examInputSx}
                          />
                        </TableCell>
                        <TableCell sx={examValueCellSx}>
                          <TextField
                            variant="standard"
                            fullWidth
                            type="number"
                            placeholder="0"
                            value={normalizeDecimal(
                              getNestedRawValue(
                                order,
                                "frame_detail.vertex_distance",
                              ),
                            )}
                            onChange={(e) =>
                              setOrderPath(
                                "frame_detail.vertex_distance",
                                e.target.value,
                              )
                            }
                            sx={examInputSx}
                          />
                        </TableCell>
                        <TableCell sx={examValueCellSx}>
                          <TextField
                            variant="standard"
                            fullWidth
                            type="number"
                            placeholder="0.0"
                            value={normalizeDecimal(
                              getNestedRawValue(
                                order,
                                "lens_detail.reading_distance",
                              ),
                            )}
                            onChange={(e) =>
                              setOrderPath(
                                "lens_detail.reading_distance",
                                e.target.value,
                              )
                            }
                            sx={examInputSx}
                          />
                        </TableCell>
                        <TableCell sx={examValueCellSx}>
                          <TextField
                            variant="standard"
                            fullWidth
                            type="number"
                            placeholder="0"
                            value={normalizeDecimal(
                              getNestedRawValue(
                                order,
                                "frame_detail.nose_bridge_value",
                              ),
                            )}
                            onChange={(e) =>
                              setOrderPath(
                                "frame_detail.nose_bridge_value",
                                e.target.value,
                              )
                            }
                            sx={examInputSx}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              </SectionCard>

              {/* <SectionCard title="Elliptical Height">
                <Box sx={{ ...examTableSx, minWidth: 0 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...examHeaderCellSx, height: 40 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[
                        {
                          key: "re",
                          path: "lens_detail.re.eh",
                          value: editableValue(reData, ["eh"]),
                        },
                        {
                          key: "le",
                          path: "lens_detail.le.eh",
                          value: editableValue(leData, ["eh"]),
                        },
                      ].map((row) => (
                        <TableRow key={row.key}>
                          <TableCell sx={examValueCellSx}>
                            <TextField
                              variant="standard"
                              fullWidth
                              value={row.value}
                              placeholder="-"
                              onChange={(e) =>
                                setOrderPath(row.path, e.target.value)
                              }
                              sx={examInputSx}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </SectionCard> */}

              <Paper
                sx={{
                  p: 2,
                  borderRadius: 1,
                  border: "1px solid #E5E7EB",
                  boxShadow:
                    "0px 1px 3px rgba(16, 24, 40, 0.08), 0px 1px 2px rgba(16, 24, 40, 0.08)",
                  backgroundColor: "#FFFFFF",
                }}
              >
                <Stack spacing={3}>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                      >
                        Coating
                      </Typography>
                      <TextField
                        variant="standard"
                        fullWidth
                        placeholder="Enter coating"
                        value={coating}
                        onChange={(e) =>
                          setField("coating", e.target.value)
                        }
                        sx={{
                          "& .MuiInput-root": {
                            fontSize: 16,
                            color: "#25272C",
                            "&:before": { borderBottomColor: "#BFC4CD" },
                            "&:hover:not(.Mui-disabled):before": {
                              borderBottomColor: "#BFC4CD",
                            },
                            "&:after": { borderBottomColor: "#1976D2" },
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                      >
                        Transition/ทำสี
                      </Typography>
                      <TextField
                        variant="standard"
                        fullWidth
                        placeholder="Enter transition"
                        value={transition}
                        onChange={(e) =>
                          setField("transition", e.target.value)
                        }
                        sx={{
                          "& .MuiInput-root": {
                            fontSize: 16,
                            color: "#25272C",
                            "&:before": { borderBottomColor: "#BFC4CD" },
                            "&:hover:not(.Mui-disabled):before": {
                              borderBottomColor: "#BFC4CD",
                            },
                            "&:after": { borderBottomColor: "#1976D2" },
                          },
                        }}
                      />
                    </Box>
                  </Stack>

                  <Box>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 2 }}
                    >
                      <Typography
                        sx={{ fontSize: 20, fontWeight: 600, color: "#25272C" }}
                      >
                        VA
                      </Typography>
                      <Stack direction="row" spacing={1.5}>
                        <Button
                          variant="contained"
                          onClick={() => setShowVaAdder((prev) => !prev)}
                          startIcon={<Add />}
                          sx={{
                            minHeight: 32,
                            px: 2,
                            borderRadius: "999px",
                            bgcolor: "#111111",
                            textTransform: "none",
                            boxShadow: "none",
                            "&:hover": {
                              bgcolor: "#000000",
                              boxShadow: "none",
                            },
                          }}
                        >
                          Add VA
                        </Button>
                        <Button
                          variant="contained"
                          disabled={selectedVaRows.length === 0}
                          onClick={handleDeleteSelectedVa}
                          startIcon={<DeleteOutline />}
                          sx={{
                            minHeight: 32,
                            px: 2,
                            borderRadius: "999px",
                            bgcolor:
                              selectedVaRows.length > 0 ? "#FFF5F4" : "#F3F4F6",
                            color:
                              selectedVaRows.length > 0 ? "#BE2217" : "#8E95A2",
                            textTransform: "none",
                            boxShadow: "none",
                            "&:hover": {
                              bgcolor:
                                selectedVaRows.length > 0
                                  ? "#FDECEC"
                                  : "#F3F4F6",
                              boxShadow: "none",
                            },
                          }}
                        >
                          Delete VA
                        </Button>
                      </Stack>
                    </Stack>

                    {showVaAdder && (
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                        sx={{ mb: 2 }}
                      >
                        <TextField
                          select
                          size="small"
                          value={addonTypeFilter}
                          onChange={(e) =>
                            setAddonTypeFilter(
                              e.target.value as ProductAddonType | "",
                            )
                          }
                          sx={{ minWidth: 160 }}
                          SelectProps={{
                            displayEmpty: true,
                            renderValue: (value) =>
                              value === "VALUE_ADD"
                                ? "V"
                                : value === "INSTRUCTION"
                                  ? "X"
                                  : "All Types",
                          }}
                        >
                          <MenuItem value="">All Types</MenuItem>
                          <MenuItem value="VALUE_ADD">V</MenuItem>
                          <MenuItem value="INSTRUCTION">X</MenuItem>
                        </TextField>
                        <Autocomplete
                          size="small"
                          sx={{ flex: 1 }}
                          options={availableAddons.filter(
                            (a) =>
                              !(order?.addons || []).some(
                                (existing) =>
                                  existing.product_addons?.id === a.id,
                              ),
                          )}
                          getOptionLabel={(opt) => {
                            const parts = [
                              opt.custom_product_search,
                              opt.custom_product,
                            ].filter(Boolean);
                            return parts.join(" - ") || opt.index_key || "";
                          }}
                          filterOptions={(x) => x}
                          onInputChange={(_e, value, reason) => {
                            if (reason === "input") {
                              fetchAddons(value);
                            }
                          }}
                          value={null}
                          onChange={(_e, val) => {
                            if (val?.id) handleAddAddon(val.id);
                          }}
                          blurOnSelect
                          clearOnBlur
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Search and add addon"
                            />
                          )}
                        />
                      </Stack>
                    )}

                    <Box
                      sx={{
                        border: "1px solid #D8DBDF",
                        borderRadius: 1,
                        overflowX: "auto",
                        overflowY: "hidden",
                        backgroundColor: "#FFFFFF",
                      }}
                    >
                      <Table
                        size="small"
                        sx={{
                          "& th, & td": {
                            borderLeft: "1px solid #D8DBDF",
                            borderBottom: "1px solid #D8DBDF",
                          },
                          "& th:first-of-type, & td:first-of-type": {
                            borderLeft: "none",
                          },
                          "& tr:last-child td": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                ...prescriptionHeaderCellSx,
                                width: 64,
                                px: 1,
                                backgroundColor: "#EDEEF1",
                              }}
                            />
                            <TableCell
                              sx={{
                                ...prescriptionHeaderCellSx,
                                width: 60,
                                backgroundColor: "#EDEEF1",
                              }}
                            >
                              Item
                            </TableCell>
                            <TableCell
                              sx={{
                                ...prescriptionHeaderCellSx,
                                width: 80,
                                backgroundColor: "#EDEEF1",
                              }}
                            >
                              Type
                            </TableCell>
                            <TableCell
                              sx={{
                                ...prescriptionHeaderCellSx,
                                width: 200,
                                textAlign: "left",
                                whiteSpace: "nowrap",
                                backgroundColor: "#EDEEF1",
                              }}
                            >
                              Search String
                            </TableCell>
                            <TableCell
                              sx={{
                                ...prescriptionHeaderCellSx,
                                textAlign: "left",
                                whiteSpace: "nowrap",
                                backgroundColor: "#EDEEF1",
                              }}
                            >
                              Description
                            </TableCell>
                            <TableCell
                              sx={{
                                ...prescriptionHeaderCellSx,
                                width: 94,
                                backgroundColor: "#EDEEF1",
                              }}
                            >
                              Quantity
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {vaRows.length > 0 ? (
                            vaRows.map((row) => {
                              const isSelected = selectedVaRows.includes(
                                row.rowId,
                              );
                              return (
                                <TableRow
                                  key={row.rowId}
                                  hover
                                  sx={{
                                    "& td": { backgroundColor: "#FFFFFF" },
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      ...prescriptionValueCellSx,
                                      width: 64,
                                      px: 1,
                                      backgroundColor: "#FFFFFF",
                                    }}
                                  >
                                    <Checkbox
                                      checked={isSelected}
                                      size="small"
                                      onChange={(_, checked) =>
                                        setSelectedVaRows((prev) =>
                                          checked
                                            ? [...prev, row.rowId]
                                            : prev.filter(
                                              (id) => id !== row.rowId,
                                            ),
                                        )
                                      }
                                      sx={{ color: "#1976D2", "&.Mui-checked": { color: "#1976D2" } }}
                                    />
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      ...prescriptionValueCellSx,
                                      width: 60,
                                      color: "#25272C",
                                    }}
                                  >
                                    {row.item}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      ...prescriptionValueCellSx,
                                      width: 80,
                                      color: "#25272C",
                                      fontSize: 13,
                                    }}
                                  >
                                    {row.addon?.type === "VALUE_ADD"
                                      ? "V"
                                      : row.addon?.type === "INSTRUCTION"
                                        ? "X"
                                        : valueOrDash(row.addon?.type)}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      ...prescriptionValueCellSx,
                                      width: 200,
                                      textAlign: "left",
                                      color: "#25272C",
                                      fontSize: 13,
                                    }}
                                  >
                                    {valueOrDash(row.addon?.custom_product_search)}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      ...prescriptionValueCellSx,
                                      textAlign: "left",
                                      color: "#25272C",
                                      fontSize: 13,
                                    }}
                                  >
                                    {valueOrDash(row.addon?.custom_product)}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      ...prescriptionValueCellSx,
                                      width: 94,
                                      color: "#25272C",
                                    }}
                                  >
                                    <TextField
                                      variant="standard"
                                      fullWidth
                                      type="number"
                                      slotProps={{ htmlInput: { min: 1, style: { textAlign: "center" } } }}
                                      value={row.quantity}
                                      onChange={(e) =>
                                        setOrderPath(
                                          `addons.${row.index}.quantity`,
                                          e.target.value === ""
                                            ? ""
                                            : Number(e.target.value),
                                        )
                                      }
                                      sx={examInputSx}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          ) : (
                            <TableRow>
                              <TableCell
                                colSpan={6}
                                sx={{
                                  py: 0,
                                  backgroundColor: "#FFFFFF",
                                }}
                              >
                                <Stack
                                  alignItems="center"
                                  justifyContent="center"
                                  spacing={1}
                                  sx={{
                                    minHeight: 220,
                                    px: 3,
                                    textAlign: "center",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: 16,
                                      fontWeight: 600,
                                      color: "#25272C",
                                    }}
                                  >
                                    No VA items yet
                                  </Typography>
                                  <Typography
                                    sx={{
                                      maxWidth: 360,
                                      fontSize: 14,
                                      color: "#8E95A2",
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    Add a VA row to populate type, search
                                    string, description, and quantity for this
                                    order.
                                  </Typography>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  </Box>
                </Stack>
              </Paper>

              <SectionCard title="Order Timeline">
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "1fr 1fr",
                    },
                    gap: 3,
                  }}
                >
                  <Stack spacing={3}>
                    <Box>
                      <Typography
                        sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                      >
                        Created Date
                      </Typography>
                      <Typography sx={{ fontSize: 16, color: "#25272C" }}>
                        {formatDateTime(createdDate)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                      >
                        Shipping Date
                      </Typography>
                      <Typography sx={{ fontSize: 16, color: "#25272C" }}>
                        {formatDateTime(trackingDate || shippingDate)}
                      </Typography>
                    </Box>
                  </Stack>
                  <Box>
                    <Typography
                      sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                    >
                      Tracking No.
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 16,
                        color: "#25272C",
                        wordBreak: "break-word",
                      }}
                    >
                      {trackingNumber || "-"}
                    </Typography>
                  </Box>
                </Box>
              </SectionCard>
            </Stack>

            <Box
              sx={{
                width: "100%",
                minWidth: 0,
                alignSelf: "start",
                position: { lg: "sticky" },
                top: { lg: 24 },
              }}
            >
              <SectionCard title="Attach Picture More">
                <Stack spacing={1.5} sx={{ width: "100%" }}>
                  {order?.extra_images?.length ? (
                    <Box
                      sx={{
                        border: "1px solid #D8DBDF",
                        borderRadius: 1,
                        height: 572,
                        overflow: "hidden",
                        bgcolor: "#FFF",
                        position: "relative",
                      }}
                    >
                      <TransformWrapper
                        minScale={1}
                        maxScale={8}
                        initialScale={1}
                        centerOnInit
                        wheel={{ step: 0.18 }}
                        pinch={{ step: 5 }}
                        doubleClick={{ disabled: true }}
                        panning={{ velocityDisabled: true }}
                      >
                        {({ zoomIn, zoomOut, resetTransform }) => (
                          <>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                zIndex: 2,
                                bgcolor: "rgba(255,255,255,0.92)",
                                border: "1px solid #D8DBDF",
                                borderRadius: 999,
                                px: 0.5,
                                py: 0.25,
                              }}
                            >
                              <IconButton
                                size="small"
                                aria-label="Zoom out"
                                onClick={() => zoomOut()}
                              >
                                <Remove fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                aria-label="Reset zoom"
                                onClick={() => resetTransform()}
                              >
                                <Refresh fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                aria-label="Zoom in"
                                onClick={() => zoomIn()}
                              >
                                <Add fontSize="small" />
                              </IconButton>
                            </Stack>
                            <TransformComponent
                              wrapperStyle={{
                                width: "100%",
                                height: "572px",
                              }}
                              contentStyle={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "12px",
                                cursor: "grab",
                              }}
                            >
                              <Box
                                component="img"
                                src={firstExtraImageSrc}
                                alt="Extra image"
                                sx={{
                                  display: "block",
                                  width: "100%",
                                  height: "100%",
                                  maxWidth: "100%",
                                  maxHeight: "100%",
                                  objectFit: "contain",
                                  userSelect: "none",
                                  WebkitUserDrag: "none",
                                  pointerEvents: "none",
                                }}
                              />
                            </TransformComponent>
                          </>
                        )}
                      </TransformWrapper>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        border: "1px solid #D8DBDF",
                        borderRadius: 1,
                        minHeight: 572,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "#F8F9FB",
                      }}
                    >
                      <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>
                        No extra images attached
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </SectionCard>
            </Box>
          </Box>

        </Stack>

        <Paper
          sx={{
            position: "fixed",
            bottom: 0,
            right: 0,
            left: { xs: 0, md: "65px" },
            zIndex: 5,
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            borderRadius: 0,
            borderTop: "1px solid #D8DBDF",
            boxShadow: "0px -4px 20px rgba(15, 23, 42, 0.05)",
            backgroundColor: "#FFFFFF",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={2}
          >
            {/* Left-side: Save / Save & Download / Download */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 1, sm: 1.5 }, alignItems: "center" }}>
              <Button
                variant="contained"
                disabled={!isDirty || submitting || savingAndDownloading}
                onClick={handleSave}
                startIcon={
                  submitting ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <Save sx={{ fontSize: { xs: 16, sm: 18 } }} />
                  )
                }
                sx={{
                  height: { xs: 36, sm: 40 },
                  borderRadius: "96px",
                  px: { xs: 2, sm: 3 },
                  fontSize: { xs: 13, sm: 14 },
                  bgcolor: "#111111",
                  color: "#FFF",
                  fontWeight: 700,
                  textTransform: "none",
                  boxShadow: "0px 1px 5px rgba(0,0,0,0.12), 0px 2px 2px rgba(0,0,0,0.14)",
                  "&:hover": { bgcolor: "#000000" },
                  whiteSpace: "nowrap",
                }}
              >
                {submitting ? "Saving…" : "Save"}
              </Button>
              <Button
                variant="contained"
                disabled={!isDirty || submitting || savingAndDownloading}
                onClick={handleSaveAndDownloadXml}
                startIcon={
                  savingAndDownloading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <Save sx={{ fontSize: { xs: 16, sm: 18 } }} />
                  )
                }
                sx={{
                  height: { xs: 36, sm: 40 },
                  borderRadius: "96px",
                  px: { xs: 2, sm: 3 },
                  fontSize: { xs: 13, sm: 14 },
                  bgcolor: "#2E7D32",
                  color: "#FFF",
                  fontWeight: 700,
                  textTransform: "none",
                  boxShadow: "0px 1px 5px rgba(0,0,0,0.12), 0px 2px 2px rgba(0,0,0,0.14)",
                  "&:hover": { bgcolor: "#1B5E20" },
                  whiteSpace: "nowrap",
                }}
              >
                {savingAndDownloading ? "Saving…" : "Save & Download XML"}
              </Button>
              <Button
                variant="outlined"
                disabled={downloadingXml || submitting || savingAndDownloading}
                onClick={handleDownloadXml}
                startIcon={
                  downloadingXml ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : null
                }
                sx={{
                  height: { xs: 36, sm: 40 },
                  borderRadius: "96px",
                  px: { xs: 2, sm: 3 },
                  fontSize: { xs: 13, sm: 14 },
                  borderColor: "#1976D2",
                  color: "#1976D2",
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "#1565C0",
                    backgroundColor: "#EBF5FF",
                  },
                  whiteSpace: "nowrap",
                }}
              >
                {downloadingXml ? "Downloading…" : "Download XML"}
              </Button>
            </Box>

            {/* Right-side actions */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 1, sm: 1.5 }, alignItems: "center", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
            {!isTerminal && (
              <Button
                variant="outlined"
                disabled={submitting}
                onClick={() =>
                  openConfirm({
                    title: "Delete Order",
                    description:
                      "Are you sure you want to delete this order? This action cannot be undone.",
                    confirmLabel: "Delete",
                    confirmColor: "#BE2217",
                    onConfirm: handleDelete,
                  })
                }
                sx={{
                  height: { xs: 36, sm: 40 },
                  borderRadius: "96px",
                  px: { xs: 2, sm: 3 },
                  fontSize: { xs: 13, sm: 14 },
                  borderColor: "#BE2217",
                  color: "#BE2217",
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "#BE2217",
                    backgroundColor: "#FFF5F4",
                  },
                }}
              >
                Delete
              </Button>
            )}
            {!isTerminal && (
              <Button
                variant="contained"
                disabled={submitting}
                onClick={openCancelDialog}
                sx={{
                  height: { xs: 36, sm: 40 },
                  borderRadius: "96px",
                  px: { xs: 2, sm: 3 },
                  fontSize: { xs: 13, sm: 14 },
                  bgcolor: "#FFFFFF",
                  color: "#BE2217",
                  border: "1px solid #BE2217",
                  fontWeight: 700,
                  boxShadow: "none",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#FFF5F4", boxShadow: "none" },
                }}
              >
                Cancel
              </Button>
            )}
            {!isTerminal && (
              <Button
                variant="contained"
                disabled={submitting}
                onClick={openStatusDialog}
                sx={{
                  height: { xs: 36, sm: 40 },
                  borderRadius: "96px",
                  px: { xs: 2, sm: 3 },
                  fontSize: { xs: 13, sm: 14 },
                  bgcolor: "#111111",
                  color: "#FFF",
                  fontWeight: 700,
                  boxShadow:
                    "0px 1px 5px rgba(0,0,0,0.12), 0px 2px 2px rgba(0,0,0,0.14), 0px 3px 1px rgba(0,0,0,0.2)",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#000000" },
                }}
              >
                Update Status
              </Button>
            )}
            </Box>
          </Stack>
        </Paper>
      </Box>

      <OrderStatusUpdateDialog
        open={statusDialog.open}
        title={statusDialog.title || "Update Status Order"}
        subtitle={statusDialog.subtitle}
        status={statusDialog.status}
        trackingNumbers={statusDialog.trackingNumbers}
        trackingDate={statusDialog.trackingDate}
        details={remarks}
        isSubmitting={submitting}
        isSubmitDisabled={statusDialog.status === order?.status}
        submitLabel={statusDialog.submitLabel}
        cancelLabel={statusDialog.cancelLabel}
        onClose={closeStatusDialog}
        onStatusChange={(status) =>
          setStatusDialog((prev) => ({ ...prev, status }))
        }
        onTrackingNumbersChange={(trackingNumbers) =>
          setStatusDialog((prev) => ({ ...prev, trackingNumbers }))
        }
        onTrackingDateChange={(trackingDate) =>
          setStatusDialog((prev) => ({ ...prev, trackingDate }))
        }
        onSubmit={() =>
          handleUpdateStatus(
            statusDialog.status,
            remarks,
            statusDialog.trackingNumbers,
            statusDialog.trackingDate,
          )
        }
      />

      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirm}
        PaperProps={{
          sx: { borderRadius: 2, minWidth: 400, p: 1 },
        }}
      >
        <DialogTitle sx={{ fontSize: 18, fontWeight: 700 }}>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          {typeof confirmDialog.description === "string" ? (
            <DialogContentText sx={{ fontSize: 14, color: "#4B5563" }}>
              {confirmDialog.description}
            </DialogContentText>
          ) : (
            confirmDialog.description
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeConfirm}
            sx={{
              borderRadius: "96px",
              px: 3,
              textTransform: "none",
              fontWeight: 700,
              color: "#6B7280",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={submitting}
            onClick={() => {
              closeConfirm();
              confirmDialog.onConfirm();
            }}
            sx={{
              borderRadius: "96px",
              px: 3,
              textTransform: "none",
              fontWeight: 700,
              bgcolor: confirmDialog.confirmColor,
              boxShadow: "none",
              "&:hover": {
                bgcolor: confirmDialog.confirmColor,
                opacity: 0.9,
                boxShadow: "none",
              },
            }}
          >
            {confirmDialog.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
}
